/**
 * Английские тексты экранов. Ключ — русский оригинал.
 *
 * Почему ключом служит сам русский текст, а не `feed.title`-подобное имя.
 * Экранов 94 и строк в них под две тысячи. Придумывать и поддерживать две
 * тысячи имён — это отдельная работа, которая ничего не даёт человеку: в
 * коде и так видно, что показывается. Зато у такого словаря есть важное
 * свойство: **непереведённая строка не ломается**, она просто остаётся
 * русской (`tx("Текст")` возвращает сам текст, если перевода нет). Поэтому
 * перевод делается по одному экрану, а не «всё или ничего».
 *
 * Что это НЕ отменяет: покрытие проверяется тестом
 * `src/i18n/coverage.test.ts` — каждая строка, обёрнутая в `tx(...)`, обязана
 * иметь перевод здесь, а список ещё не переведённых файлов лежит в тесте и
 * может только сокращаться. Так «переведено» — это проверяемое утверждение,
 * а не обещание.
 *
 * Формат: `"Русский оригинал": "English text"`. Подстановки — `{name}`,
 * одинаковые в обоих языках (см. `tx`).
 */
export const EN_CONTENT: Record<string, string> = {
  // ---- офлайн-очередь чек-листа (05.11.2026) ----
  "Не отправлено: {count}. Отправим, когда появится связь.": "{count} not sent yet. We will send it when the connection is back.",
  "Показан сохранённый чек-лист: связи нет. Отметки сохранятся на телефоне.": "Showing the saved checklist: no connection. Ticks are kept on the phone.",
  "Сервер отклонил часть отметок — открой чек-лист и проверь.": "The server rejected some ticks — open the checklist and check.",
  // ---- сводка внимания на первом экране кабинета (05.11.2026) ----
  "Не начали смену": "Shift not started",
  "Не закрыли чек-лист": "Checklist not finished",
  "Смена: {started} из {total} начали": "Shift: {started} of {total} started",
  // ---- входящий обмен по ключу (шаг 6.2) ----
  "Ключ открывает данные снаружи: чтение, а с правом записи — импорт людей и графика по CSV.": "The key opens data to the outside world: read, and with a write scope — CSV import of people and schedule.",
  "Люди (запись)": "People (write)",
  "График (запись)": "Schedule (write)",
  "Импорты по API": "API imports",
  "Ошибок: {count}": "Errors: {count}",
  "Без ошибок": "No errors",
  "Создано: {count}": "Created: {count}",
  "Обновлено: {count}": "Updated: {count}",
  "Импортов по ключу пока не было.": "No imports through the key yet.",
  // ---- уведомления: срок и эскалации (04.11.2026) ----
  "Уведомления в Telegram": "Telegram notifications",
  "Каждое приходит только по вашему выбору. Под любым сообщением есть кнопка «Отключить» — можно не заходить в приложение.": "Each one arrives only because you chose it. Every message carries a Disable button — no need to open the app.",
  "Утром: просрочки, чек-листы, проблемы": "Morning: overdue tasks, checklists, problems",
  "Перед сроком поручения": "Before a task is due",
  "Просрочка и вторая ступень — руководителю": "Overdue and second stage — for managers",
  "Напоминания о сроке": "Deadline reminders",
  "Эскалации": "Escalations",
  // ---- импорт из таблицы (шаг 6.3) ----
  "Импорт из таблицы": "Import from a spreadsheet",
  "Люди и график из CSV — без ручного заведения": "People and schedule from CSV — no manual entry",
  "Люди и график из CSV: разбор, предпросмотр, запись": "People and schedule from CSV: parse, preview, write",
  "Что импортируем": "What to import",
  "Колонки: имя, должность, подразделение, роль, telegram_id. Обязательна только «имя». Подразделение должно уже существовать.": "Columns: name, position, department, role, telegram_id. Only the name is required. The department must already exist.",
  "Колонки: имя (или telegram_id), дата, статус, начало, конец. Люди должны быть заведены заранее — сначала импорт людей.": "Columns: name (or telegram_id), date, status, start, end. People must exist already — import people first.",
  "Выбрать файл": "Choose file",
  "Скачать пример": "Download sample",
  "Или вставьте строки из таблицы (первая строка — заголовки)": "Or paste rows from the spreadsheet (the first row is the header)",
  "Перезаписать смены, которые уже стоят": "Overwrite shifts that already exist",
  "Импортировать": "Import",
  "Строк: {count}": "Rows: {count}",
  "создать": "create",
  "пропустить": "skip",
  "ошибок": "errors",
  "Разделитель: {value}": "Delimiter: {value}",
  "Подразделения: {value}": "Departments: {value}",
  "Создать": "Create",
  "Перезаписать": "Overwrite",
  "Показаны первые {shown} из {total}.": "Showing the first {shown} of {total}.",
  "Люди заведены": "People created",
  "График загружен": "Schedule imported",
  "Создано: {created}": "Created: {created}",
  "перезаписано: {updated}": "overwritten: {updated}",
  "пропущено: {skipped}": "skipped: {skipped}",
  "ошибок: {errors}": "errors: {errors}",
  "Показать ошибки": "Show errors",
  // ---- личные уведомления (04.11.2026) ----
  "Что требует внимания сегодня — и можно ли получать это в Telegram": "What needs attention today — and whether to get it in Telegram",
  "Присылать сводку в Telegram": "Get the daily digest in Telegram",
  "По умолчанию выключено: уведомления приходят только тем, кто их включил. Включённую сводку можно выключить кнопкой под сообщением.": "Off by default: notifications go only to those who turned them on. An enabled digest can be turned off with the button under the message.",
  "Не удалось изменить подписку — попробуйте ещё раз.": "Could not change the subscription — try again.",
  // ---- интеграции (шаг 6.1) ----
  "Интеграции": "Integrations",
  "Ключи API и вебхуки для внешних систем": "API keys and webhooks for external systems",
  "Ключи API": "API keys",
  "__unused_key_readonly__": "A key opens people and schedule to an outside system — read only. Shown once.",
  "Ключ создан — скопируйте сейчас, второй раз он не покажется.": "Key created — copy it now, it will not be shown again.",
  "Например: CRM": "For example: CRM",
  "Создать ключ": "Create key",
  "Отозван": "Revoked",
  "Живой": "Active",
  "Использован": "Last used",
  "Да, отозвать": "Yes, revoke",
  "Ключей пока нет.": "No keys yet.",
  "Вебхуки": "Webhooks",
  "События уходят на ваш адрес сразу, а не по опросу. Подпись — в заголовке X-SemiTask-Signature.": "Events go to your endpoint immediately, not by polling. The signature is in the X-SemiTask-Signature header.",
  "Секрет создан — им проверяется подпись. Второй раз не покажется.": "Secret created — use it to verify the signature. It will not be shown again.",
  "Адрес приёмника": "Endpoint URL",
  "Добавить вебхук": "Add webhook",
  "Ошибок подряд: {count}": "Consecutive failures: {count}",
  "Включить": "Enable",
  "Вебхуков пока нет.": "No webhooks yet.",
  "Последние доставки": "Recent deliveries",
  "Отправлено": "Sent",
  "Ошибка": "Failed",
  "В очереди": "Queued",
  "Попыток: {count}": "Attempts: {count}",
  "Доставок пока нет.": "No deliveries yet.",
  "Меню": "Menu",
  "Контакты (почта)": "Contacts (email)",
  "Новый сотрудник": "New employee",
  "Сотрудник изменён": "Employee updated",
  "Сотрудник уволен": "Employee deactivated",
  // ------------------------------------------------- «Спросить» ----------
  // Один поиск по знаниям: стандарты + пункты чек-листов.
  "Спросить": "Ask",
  "Стандарты и чек-листы одним поиском": "Search standards and checklists at once",
  "Например: как закрыть смену": "For example: how to close the shift",
  "Что найти": "What to find",
  "Найти": "Search",
  "Частые вопросы": "Common questions",
  "Ищу…": "Searching…",
  "Ничего не нашлось. Спросите руководителя — ответ стоит добавить в стандарты.":
    "Nothing found. Ask your manager — the answer belongs in the standards.",
  "Спросить: поиск по инструкциям": "Ask: search the instructions",
  "закрыть смену": "close the shift",
  "открытие зала": "opening the hall",
  "опоздание": "being late",
  // ------------------------------------------------- первый запуск -------
  // Экран «Первая смена» заменил стену из семи уроков на входе. Курс остался,
  // но открывается кнопкой.
  "Первая смена": "First shift",
  "Вот что будет сегодня.": "Here's what today looks like.",
  "Отмечаешь пункты по одному — по мере того, как сделал.":
    "You tick the items off one at a time as you go.",
  "Если руководитель что-то поручит, оно придёт сюда и напомнит о сроке.":
    "If your manager assigns something, it lands here and reminds you about the deadline.",
  "В конце дня приложение попросит закрыть смену.":
    "At the end of the day the app will ask you to close the shift.",
  "Открыть смену": "Open the shift",
  "Хочу посмотреть, как всё устроено": "Show me how it all works",
  "Как устроено приложение": "How the app works",
  "Короткие уроки — по одному действию на экран. Можно прервать.":
    "Short lessons — one action per screen. You can stop any time.",
  // ---------------------------------------------------------------- лента --
  "События смены": "Shift events",
  "Мои действия": "My actions",
  "Моё подразделение": "My department",
  "Всё предприятие": "Whole company",
  "Чужих записей здесь нет.": "No one else's entries here.",
  "Коллеги из других подразделений сюда не попадают.":
    "Colleagues from other departments don't appear here.",
  "Всё, что происходит на смене.": "Everything that happens on shift.",
  "Чек-лист закрыт": "Checklist closed",
  "Пункт чек-листа": "Checklist item",
  "Поручение": "Task",
  "Статус поручения": "Task status",
  "Фотоотчёт": "Photo report",
  "Проблема": "Issue",
  "Без даты": "No date",
  "Лента недоступна": "Feed unavailable",
  "Загружаем правила видимости…": "Loading visibility rules…",
  "{count} новых": "{count} new",
  "Поиск по имени или должности": "Search by name or position",
  "Поиск сотрудника": "Search employee",
  "Фильтр": "Filter",
  "Сбросить фильтры": "Reset filters",
  "Видны только твои действия. Расширенные фильтры доступны руководителю подразделения и управляющему.":
    "You see only your own actions. Advanced filters are available to the department manager and the owner.",
  "Пока тихо": "Quiet for now",
  "Здесь появятся отметки и поручения.":
    "Checklist marks and tasks will appear here.",
  "Предыдущий день": "Previous day",
  "Следующий день": "Next day",
  "Новые события: {count}": "New events: {count}",
  "Новые · {count}": "New · {count}",
  "Убрать фильтр {label}": "Remove filter {label}",
  "Фильтры ленты": "Feed filters",
  "Закрыть фильтры": "Close filters",
  "Фильтры": "Filters",
  "Тип события": "Event type",
  "Благодарности": "Kudos",
  "Закрыть благодарности": "Close kudos",
  "Управление предприятием": "Company management",
  "Руководителю": "For managers",
  "Закрыть": "Close",
  "Подразделение": "Department",
  "Все подразделения": "All departments",
  "Сотрудник": "Employee",
  "Поиск сотрудника в фильтре": "Search employee in filters",
  "Все сотрудники": "All employees",
  "Никого не нашли.": "Nobody found.",
  "Сбросить": "Reset",
  "Готово": "Done",
  "Тестовый сотрудник": "Test employee",
  "тест": "test",

  // --------------------------------------------------------------- задачи --
  "Мои задачи": "My tasks",
  "Привет, {name}": "Hi, {name}",
  "База стандартов": "Standards",
  "Приёмка работ": "Work review",
  "Передача смены": "Shift handover",
  "Что нужно сделать сегодня": "What to do today",
  "задач {count}": "{count} tasks",
  "просрочено {count}": "{count} overdue",
  "Активные": "Active",
  "Все": "All",
  "Смена сегодня": "Shift today",
  "Смена закрыта": "Shift closed",
  "Открыть чек-лист": "Open checklist",
  "Посмотреть чек-лист": "View checklist",
  "Продолжить чек-лист": "Continue checklist",
  "Начать смену": "Start shift",
  "Проблемы": "Issues",
  "открыто {count}": "{count} open",
  "{count} ждут внимания: срок или повтор.":
    "{count} need attention: deadline or repeat.",
  "Все в работе — открой журнал, если нужен статус.":
    "All in progress — open the log if you need a status.",
  "На сегодня всё": "All done for today",
  "Задач нет — можно проверить чек-лист смены.":
    "No tasks — you can go through the shift checklist.",

  // статусы, приоритеты и причины
  "Новое": "New",
  "Принято": "Accepted",
  "В работе": "In progress",
  "На проверке": "On review",
  "На доработке": "Rework",
  "Выполнено": "Done",
  "Просрочено": "Overdue",
  "Не выполнено": "Not done",
  "низкий": "low",
  "обычный": "normal",
  "высокий": "high",
  "Не хватает времени": "Not enough time",
  "Нужна помощь": "Need help",
  "Нет материалов/доступа": "No materials/access",
  "Оборудование не работает": "Equipment is broken",
  "Другое": "Other",

  // карточка поручения
  "без срока": "no deadline",
  "сегодня {time}": "today {time}",
  "завтра {time}": "tomorrow {time}",
  "вчера {time}": "yesterday {time}",
  "смена {date}": "shift {date}",
  "не сегодня · {label}": "not today · {label}",
  "приоритет: {label}": "priority: {label}",
  "фото есть": "photo attached",
  "нужен фотоотчёт": "photo required",
  "с приёмкой": "with review",
  "просрочено": "overdue",
  "сделали {done} из {total}": "{done} of {total} done",
  "Моя часть:": "My part:",
  "Результат:": "Expected result:",
  "Работа сдана — ждёт приёмки руководителя.":
    "Work submitted — waiting for the manager's review.",
  "Принять": "Accept",
  "Начать": "Start",
  "Взять в работу": "Start work",
  "Сдать на проверку": "Submit for review",
  "Отмена": "Cancel",
  "Не успеваю": "I'm running late",
  "Добавить фото": "Add photo",
  "Руководителю отправлено объяснение. Срок отсчитывается заново.":
    "The manager got your explanation. The deadline counts from now.",
  "Причина": "Reason",
  "Когда сможешь закончить (необязательно)": "When can you finish (optional)",
  "Комментарий": "Comment",
  "Что именно мешает?": "What exactly is blocking you?",
  "Отправить руководителю": "Send to manager",
  "Фотоотчёт: сними или выбери фото — поручение закроется само.":
    "Photo report: take or pick a photo — the task will close itself.",

  // ------------------------------------------------------------ чек-лист --
  "Чек-лист смены": "Shift checklist",
  "Чек-лист": "Checklist",
  "Открытие": "Opening",
  "Закрытие": "Closing",
  "Первый день": "First day",
  "Сегодня работаешь:": "You're working today:",
  "сменить": "change",
  "В каком зале работаешь?": "Which hall are you working in?",
  "У каждого зала свой чек-лист открытия. Выбор запомнится на сегодня — сменить можно в любой момент.":
    "Each hall has its own opening checklist. Your choice is kept for today — you can change it at any time.",
  // Запасное слово для термина отрасли, когда руководитель его не менял.
  "Залы": "Halls",
  "{halls} не заведены. Попроси руководителя добавить их в разделе «Чек-листы».":
    "No halls yet. Ask your manager to add them under “Checklists”.",
  "выбран": "selected",
  "выбрать": "select",
  "Смена закрыта. Спасибо за работу!": "Shift closed. Thanks for your work!",
  "Смена открыта. Хорошей смены!": "Shift opened. Have a good shift!",
  "закрытия смены": "for closing the shift",
  "открытия зала": "for opening the hall",
  "первого дня": "for the first day",
  "Попроси руководителя назначить шаблон {variant}.":
    "Ask your manager to assign a checklist template {variant}.",
  "Не сегодня — {label}": "Not today — {label}",
  "по расписанию": "on schedule",
  "Пункт закрывается фотоотчётом": "This item closes with a photo report",
  "Отметить пункт": "Mark item",
  "(опц.)": "(optional)",
  "фотоотчёт приложен": "photo attached",
  "нужен фотоотчёт: снимок с камеры или из галереи":
    "photo required: take one or pick from the gallery",
  "Снять отчёт": "Take report photo",
  "закрыто в {time}": "closed at {time}",
  "Заметка": "Note",
  "Например: инвентарь в порядке, не хватило 2 упаковок":
    "For example: stock is fine, but 2 packs were missing",
  "Сохранить заметку": "Save note",

  // ---------------------------------------------------------- отчёт смены --
  "Отчёт смены": "Shift report",
  "Назад в профиль": "Back to profile",
  "Срез на дату": "Snapshot by date",
  "Дата смены": "Shift date",
  "Все отделы": "All departments",
  "Видно все отделы": "All departments visible",
  "Видно своё подразделение": "Own department visible",
  "людей {count}": "people: {count}",
  "Смен начато": "Shifts started",
  "из {count}": "of {count}",
  "Смен закрыто": "Shifts closed",
  "чек-лист на 100%": "checklist at 100%",
  "Поручений": "Tasks",
  "выполнено {count}": "{count} done",
  "сдано, но не принято": "submitted, not accepted",
  "за эту смену": "this shift",
  "Пробелы стандартов": "Standards gaps",
  "обязательные не сданы": "required not passed",
  "Сейчас в работе": "In progress now",
  "{tasks} поручений · {issues} проблем": "{tasks} tasks · {issues} issues",
  "Новых проблем за смену: {count}": "New issues this shift: {count}",
  "По отделам": "By department",
  "Обязательные стандарты": "Required standards",
  "только пробелы": "gaps only",
  "Обязательных стандартов пока нет — отметь их в базе стандартов.":
    "No required standards yet — mark them in the standards base.",
  "Все обязательные стандарты сданы.": "All required standards passed.",
  "{count} чел.": "{count} people",
  "Смены {started}/{employees} · закрыто {done}":
    "Shifts {started}/{employees} · closed {done}",
  "поручений {count}": "tasks {count}",
  "на проверке {count}": "on review {count}",
  "проблем {count}": "issues {count}",
  "пробелов {count}": "gaps {count}",
  "обязательный": "required",
  "Не сдали: {names}": "Not passed: {names}",
  "Только {name}": "Only {name}",
  "Файл готов: {name}": "File is ready: {name}",
  "Не получилось скачать файл — попробуй ещё раз.":
    "Couldn't download the file — please try again.",

  // ------------------------------------------------------------- команда --
  "Команда": "Team",
  "Назад": "Back",
  "Сводка смены": "Shift summary",
  "все подразделения": "all departments",
  "моё подразделение": "my department",
  "Смена начата": "Shift started",
  "Закрыто": "Closed",
  "Требует внимания": "Needs attention",
  "и ещё {count}": "and {count} more",
  "Подразделения": "Departments",
  "Подразделений пока нет.": "No departments yet.",
  "Сотрудники": "Employees",
  "Позвать в команду": "Invite to the team",
  "Ссылка-приглашение или код компании: человек входит сам и сразу попадает в свой отдел.":
    "An invite link or the company code: the person signs up alone and lands in their department right away.",
  "Пригласить сотрудника": "Invite an employee",
  "вход по почте": "signs in by email",
  "Найти человека или должность": "Find a person or position",
  "Поиск по сотрудникам": "Search employees",
  "Сотрудников пока нет.": "No employees yet.",
  "Последние действия": "Recent activity",
  "Рабочие часы и уведомления": "Work hours and notifications",
  "смена закрыта": "shift closed",
  "не закрыта вовремя": "not closed on time",
  "в работе": "in progress",
  "не начинал": "not started",
  "закрыто {count}": "closed {count}",
  "Открыть карточку": "Open card",
  "Загружаем…": "Loading…",
  "Сохранить часы": "Save hours",
  "Алерты только в рабочее время": "Alerts during work hours only",
  "Вторая ступень: пинг админам": "Second level: ping admins",
  "Если руководитель молчит": "If the manager stays silent",
  "Порог второй ступени, часы": "Second level threshold, hours",
  "{hours} ч": "{hours} h",
  "Напоминать в каждой смене": "Remind on every shift",
  "Мьют до {when}": "Muted until {when}",
  "Мьют выключен": "Mute is off",
  "24ч": "24h",
  "3 дня": "3 days",
  "7 дней": "7 days",
  "30 дней": "30 days",
  "Снять": "Clear",
  "Ночью уведомления не уходят в чат, но копятся в приложении — утренний разбор не потеряется.":
    "At night notifications don't go to chat but pile up in the app — the morning review won't be lost.",
  "Сбрасывать нечего — всё уже закрыто.": "Nothing to reset — everything is already closed.",
  "Удалить {tasks} поручений и {issues} проблем?":
    "Delete {tasks} tasks and {issues} issues?",
  "Точно? Действие необратимо. Завершённые поручения и история останутся.":
    "Are you sure? This cannot be undone. Completed tasks and history stay.",
  "Удалено: поручений {tasks}, проблем {issues}.":
    "Deleted: {tasks} tasks, {issues} issues.",
  "Данные изменились, пока ты подтверждал. Открой сброс заново.":
    "The data changed while you were confirming. Open the reset again.",
  "Опасная зона": "Danger zone",
  "Убирает незакрытые поручения и открытые проблемы. Завершённые задачи, KPI и история сдач остаются; тестовый пул сбрасывается отдельно.":
    "Removes open tasks and open issues. Completed tasks, KPIs and submission history stay; the test pool is reset separately.",
  "Считаем…": "Counting…",
  "Сбросить текущую работу": "Reset current work",

  // -------------------------------------------------------------- приёмка --
  "Приёмка": "Work review",
  "Назад к задачам": "Back to tasks",
  "Работ на проверке нет": "Nothing waiting for review",
  "Ждут приёмки: {count}": "Waiting for review: {count}",
  "Всё принято": "All accepted",
  "Когда сотрудник сдаст поручение с проверкой, оно появится здесь.":
    "When someone submits a task that needs review, it shows up here.",
  "сдано {count}": "submitted {count}",
  "фотоотчёт": "photo report",
  "Все, кто сдал": "Everyone who submitted",
  "Часть: {part}": "Part: {part}",
  "Сдано: {when}": "Submitted: {when}",
  "На доработку": "Send back",
  "Что переделать — коротко и по делу": "What to redo — short and to the point",
  "Вернуть с комментарием": "Send back with a comment",
  "один за всех": "one for all",
  "каждый сам": "everyone on their own",
  "части работы": "work parts",
  // ---------------------------------------------------------- передача смены --
  "Передача смены не открылась": "Couldn't open the handover",
  "Тебе передали смену: {count} — нужно подтвердить.": "A shift was handed to you: {count} — needs confirming.",
  "Чужих хвостов на тебе нет. Сдай свои, когда уходишь.": "No one else's loose ends on you. Hand over your own when you leave.",
  "Что осталось открытым и кому это передали.": "What's still open and who it went to.",
  "Сдать смену": "Hand over shift",
  "Передач пока нет": "No handovers yet",
  "Нажми «Сдать смену»: приложение соберёт открытые поручения и проблемы, а следующий человек подтвердит приём.": "Tap “Hand over shift”: the app collects open tasks and issues, and the next person confirms the handover.",
  "смена {date} · {when}": "shift {date} · {when}",
  "Открытых пунктов: {count}": "Open items: {count}",
  "Открытых пунктов нет": "No open items",
  "Загружаю…": "Loading…",
  "На момент сдачи открытых пунктов не было.": "There were no open items at the time of handover.",
  "Возвращено: {reason}": "Sent back: {reason}",
  "Принял(а): {name} · {when}": "Accepted by: {name} · {when}",
  "Принять смену": "Accept shift",
  "Вернуть": "Send back",
  "Почему не принимаешь? Без причины вернуть нельзя.": "Why aren't you accepting? A reason is required to send it back.",
  "Вернуть с причиной": "Send back with a reason",
  "Кому передаёшь": "Who takes over",
  "Загружаю коллег…": "Loading colleagues…",
  "Некому передать: в отделе нет других активных сотрудников.": "Nobody to hand over to: no other active staff in the department.",
  "Что уйдёт в сдачу": "What goes into the handover",
  " — поручений {tasks}, проблем {issues}": " — tasks {tasks}, issues {issues}",
  "Собираю открытые пункты…": "Collecting open items…",
  "Открытых пунктов нет — передашь только заметку.": "No open items — only the note will be passed on.",
  "Заметка следующему (необязательно)": "Note for the next person (optional)",
  "Что важно знать: ключи, гости, недоделанное": "What matters: keys, guests, unfinished work",
  "Передаю…": "Handing over…",
  "Передать смену": "Hand over shift",
  "Принимающий получит уведомление и подтвердит приём — или вернёт с причиной. Снимок открытых пунктов сохранится в истории.": "The person taking over gets a notification and confirms — or sends it back with a reason. A snapshot of the open items stays in the history.",
  // ---------------------------------------------------------- профиль сотрудника --
  "Профиль готов. Смена начнётся с чек-листа.": "Profile ready. Your shift starts with the checklist.",
  "Сохранили — остался один шаг.": "Saved — one step left.",
  "Изменить мои данные": "Edit my details",
  "Изменить": "Edit",
  "Доступ": "Access",
  "Опыт": "Experience",
  "новичок": "newcomer",
  "с опытом": "experienced",
  "выбери ниже": "pick below",
  "не указан": "not set",
  "Профиль открыт": "Profile visible",
  "Профиль закрыт": "Profile hidden",
  "Коллеги видят твою активность и цифры. Руководитель видит их всегда.": "Colleagues see your activity and numbers. Your manager always sees them.",
  "Коллеги видят только имя и отдел. Руководитель и админ — всё.": "Colleagues see only your name and department. Managers and admins see everything.",
  "Открыт": "Visible",
  "Закрыт": "Hidden",
  "Закончим знакомство": "Finish getting started",
  "Мои данные": "My details",
  "Подразделение определяет чек-листы, поручения и ссылки, которые тебе приходят.": "Your department decides which checklists, tasks and links you get.",
  "Продолжить": "Continue",
  "Сохранить": "Save",
  "Опыт и обучение": "Experience and training",
  "Курс": "Course",
  "Три уровня от знакомства до самостоятельной работы — по одному действию на урок.": "Three levels from first look to working solo — one action per lesson.",
  "Учимся вместе · вкл": "Learning together · on",
  "Учимся вместе · выкл": "Learning together · off",
  "Открыть курс": "Open the course",
  "Продолжить курс": "Continue the course",
  "Все разделы": "All sections",
  "Для администратора": "For the admin",
  "Примерить роль": "Try a role",
  "Роль меняет только твой обзор: задачи, ленту и кабинет. Доступ в админку остаётся — вернуть себя можно этой же кнопкой.": "The role changes only your view: tasks, feed and workspace. Admin access stays — the same button brings you back.",
  "Режим проверки": "Review mode",
  "Открой приложение глазами сотрудника. Тестовые задачи и смены удалятся при выходе — настоящие данные не изменятся.": "Open the app through an employee's eyes. Test tasks and shifts are removed on exit — real data stays untouched.",
  "Кого проверяем": "Who to check",
  "Выбери сотрудника": "Pick an employee",
  "Открываем…": "Opening…",
  "Открыть как сотрудник": "Open as employee",
  "Тестовый пул": "Test pool",
  "Сотрудники `[BOT]` для проверки сценариев. Настоящие данные не затрагиваются.": "`[BOT]` employees for testing scenarios. Real data isn't touched.",
  "Открыть тестовый пул": "Open the test pool",
  "Вернуться в чат с ботом": "Back to the bot chat",
  "Сервер": "Server",
  "Если основной адрес недоступен, укажи резервный — приложение продолжит работать без переустановки. Пусто — адрес из сборки.": "If the main address is unreachable, set a fallback — the app keeps working without reinstalling. Empty means the build address.",
  "Сохранить и перезагрузить": "Save and reload",
  "Почта не подтверждена": "Email not verified",
  "Письмо отправлено. Ссылка живёт двое суток — открой её на любом устройстве, вход не нужен.": "Email sent. The link lives for two days — open it on any device, no sign-in needed.",
  "Отправляю…": "Sending…",
  "Подтвердить почту": "Verify email",
  // ---------------------------------------------------------- карточка и достижения --
  "Карточка сотрудника": "Employee card",
  "Закрыть карточку": "Close the card",
  "что делал на смене": "what they did on shift",
  "Тебя никто не видит в карточке — открой профиль в настройках.": "Nobody sees you in the card — open your profile in settings.",
  "Человек сам решает, показывать ли активность и цифры. Руководитель и администратор видят её всегда.": "People decide for themselves whether to show their activity and numbers. Managers and admins always see them.",
  "Всего опыта": "All-time experience",
  "За неделю": "This week",
  "Место в неделе": "Weekly place",
  "{place} из {total}": "{place} of {total}",
  "Пунктов чек-листа": "Checklist items",
  "Поручений закрыто": "Tasks completed",
  "Пока пусто: человек ещё ничего не отмечал.": "Nothing yet: the person hasn't ticked anything.",
  "Профиль закрыт для коллег, но тебе он виден: ты руководитель или администратор.": "The profile is hidden from colleagues, but you can see it: you're a manager or admin.",
  "Личный прогресс": "Personal progress",
  "{count} шт.": "{count} pcs",
  "пока пусто": "empty for now",
  "Счётчик для себя: он растёт от закрытых поручений и отработанных смен. Гостям и коллегам блок пока не виден.": "A counter for you: it grows with completed tasks and worked shifts. Guests and colleagues can't see this block yet.",
  "поручений": "tasks",
  "рабочих дней": "workdays",
  "пунктов": "items",
  "Опыт смены": "Shift experience",
  "максимальное звание": "top rank",
  "Прогресс до следующего звания": "Progress to the next rank",
  "Сотрудник недели": "Employee of the week",
  "Опыт дают закрытые пункты чек-листа, полностью закрытая смена и поручения. Вовремя закрытое поручение даёт надбавку.": "Experience comes from ticked checklist items, a fully closed shift and tasks. A task closed on time earns a bonus.",
  // ---------------------------------------------------------- обратная связь --
  "Спасибо! Ответы ушли руководителю.": "Thanks! Your answers went to the manager.",
  "Обратная связь": "Feedback",
  "Как прошла смена": "How did the shift go",
  "Ответы отправлены": "Answers sent",
  "Вопросов пока нет — администратор их ещё не завёл.": "No questions yet — the admin hasn't added any.",
  "Пара слов своими словами": "A few words in your own words",
  "Отправляем…": "Sending…",
  "Отправить": "Send",
  "Ответов ОС": "Feedback replies",
  // ---------------------------------------------------------- тестовый пул --
  "Сгенерировали: смен {shifts}, поручений {tasks}.": "Generated: {shifts} shifts, {tasks} tasks.",
  "Следы тестового пула удалены.": "Test pool traces removed.",
  "Следы пула": "Pool traces",
  "видны": "visible",
  "скрыты": "hidden",
  "Тумблер прячет пул из ленты, списков и отчётов, не удаляя данные. Один тап — и боевая картина чистая.": "The toggle hides the pool from the feed, lists and reports without deleting data. One tap and the live picture is clean.",
  "Скрыть следы": "Hide traces",
  "Показать следы": "Show traces",
  "Пул не заведён.": "The pool hasn't been created.",
  "Смен": "Shifts",
  "Завести пул": "Create pool",
  "Симулировать 3 смены": "Simulate 3 shifts",
  "Удаление": "Deletion",
  "Обратной операции нет. «Активность» убирает смены, поручения и ответы, а «вместе с сотрудниками» удаляет и самих тестовых людей с их строками в базе.": "This can't be undone. “Activity” removes shifts, tasks and answers, while “with employees” also deletes the test people and their database rows.",
  "Снести всю активность тестового пула? Сотрудники останутся.": "Wipe all test-pool activity? Employees stay.",
  "Снести только активность": "Wipe activity only",
  "Удалить тестовых сотрудников вместе со всей их активностью?": "Delete the test employees along with all their activity?",
  "Удалить сотрудников и следы": "Delete employees and traces",
  // ---------------------------------------------------------- ссылки и материалы --
  "Полезные ссылки": "Useful links",
  "Общие и для подразделения «{name}»": "Shared and for the “{name}” department",
  "Инструкции, регламенты и рабочие сервисы": "Guides, rules and work services",
  "Материалы смены": "Shift materials",
  "График, меню и методички — открываются без Google": "Rota, menu and guides — open without Google",
  "Ссылок пока нет": "No links yet",
  "Руководитель добавит их в админке — здесь появится список.": "A manager will add them in the admin panel — the list will appear here.",
  "График, меню и методички. Показываем копию с сервера — Google открывать не нужно.": "Rota, menu and guides. We show a copy from the server — no need to open Google.",
  "Материалов пока нет": "No materials yet",
  "Руководитель добавит таблицы — они появятся здесь.": "A manager will add sheets — they'll appear here.",
  "ещё ни разу": "never yet",
  "· устарело": "· outdated",
  "Обновляю…": "Refreshing…",
  "⟳ Обновить": "⟳ Refresh",
  "Открыть таблицу": "Open the sheet",
  "Данных ещё нет: таблица не отдала CSV. Проверь, что доступ «всем, у кого есть ссылка».": "No data yet: the sheet didn't return a CSV. Check that access is set to “anyone with the link”.",
  "Таблица материалов смены": "Shift materials sheet",
  // ---------------------------------------------------------- знакомство и обучение --
  "Не загрузилось": "Couldn't load",
  "Шаг {step} из {total}": "Step {step} of {total}",
  "Знакомство": "Getting started",
  "Пара шагов — и можно начинать смену.": "A couple of steps and you can start your shift.",
  "Где ты работаешь?": "Where do you work?",
  "От этого зависят чек-лист смены и задачи.": "The shift checklist and tasks depend on it.",
  "Сохраняем…": "Saving…",
  "Как давно ты в деле?": "How long have you been at this?",
  "Я только начинаю": "I'm just starting",
  "Покажем всё по шагам": "We'll walk you through step by step",
  "Уже освоился": "Already comfortable",
  "Сразу к работе": "Straight to work",
  "Освоимся за пару минут": "It takes a couple of minutes",
  "Я уже запомнил": "I've got it memorised",
  "Обязательные чек-листы и задачи это не отменяет.": "Mandatory checklists and tasks still apply.",
  "Курс сброшен — можно пройти заново с первого урока.": "Course reset — you can start over from the first lesson.",
  "Курс пройден. Осталось выбрать, как продолжим.": "Course complete. One choice left on how to continue.",
  "Обучение": "Training",
  "Три уровня, по одному действию на экран.": "Three levels, one action per screen.",
  "Сегодня учебная смена": "Today is a training shift",
  "Ставка частичная.": "Partial shift.",
  "Ставка полная.": "Full shift.",
  "Отлично, первый этап пройден.": "Nice, the first step is done.",
  "Теперь решим, как продолжим в следующую смену.": "Now let's decide how to continue next shift.",
  "Повторим вместе": "Let's go through it together",
  "Обязательные чек-листы и поручения остаются в любом режиме.": "Mandatory checklists and tasks stay in any mode.",
  "Готово. Можно возвращаться к смене.": "Done. You can get back to the shift.",
  "Осталось {count}. Уроки можно проходить в любом порядке.": "{count} left. Lessons can be taken in any order.",
  "Пройти курс заново": "Retake the course",
  "Снимет отметки уроков, но оставит подразделение и опыт. Смена, задачи и отметки чек-листа не затрагиваются.": "Clears lesson marks but keeps the department and experience. The shift, tasks and checklist marks are untouched.",
  "Сбросить прогресс обучения и начать курс заново?": "Reset training progress and start the course over?",
  "Точно? Отметки уроков будут удалены.": "Sure? Lesson marks will be deleted.",
  "Сбрасываю…": "Resetting…",
  "Сбросить прогресс обучения": "Reset training progress",
  "Понятно, отметил": "Got it, marked",
  "Пройдено": "Done",
  // ---------------------------------------------------------- push и веб-версия --
  "Браузер не разрешил уведомления. Это меняется в настройках сайта — тогда вернись и включи снова.": "The browser didn't allow notifications. You can change that in the site settings — then come back and turn them on again.",
  "Уведомления в браузере": "Browser notifications",
  "Напоминание о сроке придёт на телефон, даже если приложение закрыто. Telegram для этого не нужен.": "A deadline reminder reaches your phone even with the app closed. Telegram isn't needed for that.",
  "Подожди…": "Hold on…",
  "Выключить на этом устройстве": "Turn off on this device",
  "Включить уведомления": "Turn on notifications",
  "Разрешение уже запрещено в браузере. Открой настройки сайта и разреши уведомления — кнопка выше сработает.": "Permission is already blocked in the browser. Open the site settings, allow notifications, and the button above will work.",
  "Проверочное уведомление отправлено.": "Test notification sent.",
  "Подписка сохранена, но push-сервис не принял отправку. Попробуй выключить и включить снова.": "The subscription is saved, but the push service rejected the send. Try turning it off and on again.",
  "Проверить": "Send test",
  "Веб-версия": "Web version",
  "На компьютере окно Telegram узкое. Открой ссылку в браузере — там широкий экран с разделами и сводкой смены.": "Telegram's window is narrow on a computer. Open the link in a browser — you get a wide screen with sections and the shift summary.",
  "На телефоне ссылку можно поставить на домашний экран: в Safari — «Поделиться → На экран Домой», в Chrome — «Установить приложение». Откроется без адресной строки, как обычная программа.": "On a phone you can add the link to your home screen: in Safari — Share → Add to Home Screen, in Chrome — Install app. It opens without an address bar, like a regular app.",
  "Готовлю ссылку…": "Preparing the link…",
  "Создать ссылку для компьютера": "Create a desktop link",
  "Скопировано": "Copied",
  "Скопировать": "Copy",
  "Открыть": "Open",
  "Отозвать ссылку? Браузер, где она открыта, потеряет доступ.": "Revoke the link? The browser it's open in loses access.",
  "Выйти из веб-версии на этом устройстве": "Sign out of the web version on this device",
  "ещё не открывалась": "not opened yet",
  "Ссылка": "Link",
  " · это устройство": " · this device",
  "отозвана": "revoked",
  "Отозвать": "Revoke",
  // ---------------------------------------------------------- стандарты --
  "Стандарты": "Standards",
  "Инструкции и короткие тесты по ним": "Instructions with short tests",
  "+ Стандарт": "+ Standard",
  "Стандартов пока нет": "No standards yet",
  "Руководитель добавит инструкции — здесь появятся они и тесты к ним.": "A manager will add instructions — they and their tests will appear here.",
  "Сдано стандартов": "Standards passed",
  "сдано": "passed",
  "не сдано": "not passed",
  "Назад к списку": "Back to the list",
  "Стандарт": "Standard",
  "{category} · порог {percent}%": "{category} · pass mark {percent}%",
  "Обязательный: не сдавшие попадают в отчёт смены": "Required: those who don't pass show up in the shift report",
  "Сделать обязательным для отчёта смены": "Make it required for the shift report",
  "Теста к этому стандарту нет — достаточно прочитать инструкцию.": "There's no test for this standard — reading the instructions is enough.",
  "Ответ неверный — вернись к инструкции выше.": "That's wrong — go back to the instructions above.",
  "Сдано": "Passed",
  "Пока не сдано": "Not passed yet",
  " Перечитай инструкцию и попробуй снова.": " Read the instructions again and try once more.",
  "Попробовать снова": "Try again",
  "Проверить ответы": "Check answers",
  "Новый стандарт": "New standard",
  "Пиши как для нового сотрудника: что сделать, в каком порядке и почему.": "Write it for a new employee: what to do, in what order and why.",
  "Название": "Name",
  "Гигиена рук перед сменой": "Hand hygiene before the shift",
  "Значок": "Icon",
  "Категория": "Category",
  "Короткое описание (для списка)": "Short description (for the list)",
  "Инструкция": "Instructions",
  "1. Помой руки…": "1. Wash your hands…",
  "Обязательный: не сдавшие попадут в пробелы отчёта смены.": "Required: those who don't pass show up as gaps in the shift report.",
  "Тест": "Test",
  "Вопрос {number}": "Question {number}",
  "Удалить вопрос": "Delete question",
  "Верный вариант": "Correct option",
  "Вариант": "Option",
  "+ Вопрос": "+ Question",
  "Сохранить стандарт": "Save standard",
  // ---------------------------------------------------------- проблемы --
  "Журнал проблем не открылся": "Couldn't open the issue log",
  "Журнал проблем": "Issue log",
  "Что на смене пошло не так — и кто это закрывает.": "What went wrong on shift — and who's closing it.",
  "+ Сообщить": "+ Report",
  "У тебя открытых проблем нет": "You have no open issues",
  "Журнал пуст": "The log is empty",
  "Сломалось оборудование, закончились расходники, гость недоволен — заведи карточку, чтобы смена не теряла проблему.": "Equipment broke, supplies ran out, a guest is unhappy — create a card so the shift doesn't lose track of it.",
  "висит {age}": "open {age}",
  "· завёл {name}": "· reported by {name}",
  "· на {name}": "· to {name}",
  "· из поручения": "· from a task",
  "Новая проблема": "New issue",
  "Что случилось": "What happened",
  "Например: не работает терминал на баре": "For example: the bar terminal is down",
  "Насколько срочно": "How urgent",
  "Где (необязательно)": "Where (optional)",
  "Зал, кухня, бар, склад…": "Floor, kitchen, bar, store…",
  "Подробности (необязательно)": "Details (optional)",
  "Что уже пробовали, кому сказали, что нужно для решения": "What's been tried, who was told, what's needed to fix it",
  "Записываю…": "Saving…",
  "Завести карточку": "Create card",
  "Руководитель отдела увидит проблему в колокольчике и в журнале. В чат сообщение не уходит — это не срочный алерт.": "The department manager sees the issue in the bell and in the log. Nothing goes to the chat — this isn't an urgent alert.",
  "Завёл {name} · смена {date} · висит {age}": "Reported by {name} · shift {date} · open {age}",
  " · ответственный: {name}": " · owner: {name}",
  "Решено": "Resolved",
  "Переоткрыть": "Reopen",
  "Назначить на себя": "Assign to me",
  "История": "History",
  "система": "system",
  "Добавить комментарий": "Add a comment",
  // ---------------------------------------------------------- табель смен --
  "пропущено занятых: {count}": "busy cells skipped: {count}",
  "Записано смен": "Shifts saved",
  "Скопировано смен": "Shifts copied",
  "Табель смен": "Timesheet",
  "можно править": "editable",
  "твой отдел": "your department",
  "Предыдущая неделя": "Previous week",
  "Сегодня": "Today",
  "Следующая неделя": "Next week",
  "Шаблон недели и массовые правки": "Week template and bulk edits",
  "В этом отделе пока нет сотрудников.": "No one in this department yet.",
  "Табель смен на неделю": "Shift timesheet for the week",
  "у": "T",
  "— учебная смена": "— training shift",
  "Удалить шаблон недели?": "Delete the week template?",
  "Снять смены с выбранных клеток ({count})?": "Clear shifts from the selected cells ({count})?",
  "Удалить смену из табеля?": "Delete the shift from the timesheet?",
  "Без подразделения": "No department",
  " · учебная": " · training",
  "Смены нет": "No shift",
  " · перенесена с {date}": " · moved from {date}",
  "Статус:": "Status:",
  "Часы:": "Hours:",
  "Заметка:": "Note:",
  "Править табель может руководитель подразделения.": "Only the department manager can edit the timesheet.",
  "Начало": "Start",
  "Конец": "End",
  "Заметка (необязательно)": "Note (optional)",
  "Перенести на дату (необязательно)": "Move to a date (optional)",
  "Сохранить · {status}": "Save · {status}",
  "Удалить": "Delete",
  // ---------------------------------------------------------- шаблон недели --
  "Шаблон недели": "Week template",
  " · смена сразу на много клеток": " · shift across many cells at once",
  "Шаблоны подразделения": "Department templates",
  "Применить": "Apply",
  "Кому": "For whom",
  "Снять всех": "Clear all",
  "Выбрать всех": "Select all",
  "В какие дни": "Which days",
  "Выбери хотя бы один день — без этого записывать нечего.": "Pick at least one day — otherwise there's nothing to save.",
  "Дней выбрано: {count}": "Days selected: {count}",
  "Заменить уже поставленные смены": "Replace shifts already scheduled",
  "Выключено — заполняем только пустые клетки.": "Off — only empty cells are filled.",
  "Записываем…": "Saving…",
  "{people} чел. × {days} дн.": "{people} people × {days} days",
  "Выбери людей и дни": "Pick people and days",
  "Название шаблона": "Template name",
  "Отменить сохранение шаблона": "Cancel saving the template",
  "Сохранить подборку как шаблон": "Save the selection as a template",
  "Копируем…": "Copying…",
  "Скопировать прошлую неделю": "Copy last week",
  "Снимаем…": "Clearing…",
  "Снять смены": "Clear shifts",
  "Копирование переносит прошлую неделю на эту и не затирает занятые дни. «Снять смены» трогает только выбранные дни. Шаблон применяется своим рисунком недели — выбранные дни для него не важны.": "Copying moves last week onto this one and doesn't overwrite busy days. “Clear shifts” touches only the selected days. A template applies its own week pattern — the selected days don't matter for it.",
  // ---------------------------------------------------------- приветствие --
  "Не показывать приветствие? Вернуть можно в настройках профиля.": "Hide the welcome? You can bring it back in profile settings.",
  "Скрыть приветствие": "Hide the welcome",
  "Не показывать в следующий раз": "Don't show next time",
  // ---------------------------------------------------------- общее: каркас и приложение --
  "Telegram не подтвердил вход": "Telegram didn't confirm the sign-in",
  "Подпись мини-аппа не принята: бот могли переподключить к другой организации. Открой приложение заново из бота — или возьми ссылку для компьютера командой": "The mini app signature wasn't accepted: the bot may have been reconnected to another organisation. Reopen the app from the bot — or get a desktop link with the command",
  "Проверить ещё раз": "Check again",
  "Организация на паузе": "Organisation on pause",
  "Доступ приостановлен на стороне организации. Напиши управляющему: когда паузу снимут, вход заработает снова — пароль менять не надо.": "Access is suspended on the organisation's side. Message the owner: once the pause is lifted, sign-in works again — no need to change your password.",
  "Тебя нет в списке шестёрки": "You're not on the core team list",
  "Скинь админу свой Telegram ID командой /whoami боту.": "Send your Telegram ID to the admin with the /whoami command in the bot.",
  "Ошибка авторизации": "Sign-in error",
  "Не удалось загрузить участников": "Couldn't load the members",
  "Встречи и опросы": "Meetings and polls",
  "RSVP по встречам и голосования за слот — всё в одном месте.": "Meeting RSVPs and slot votes — all in one place.",
  "Админка": "Admin",
  "Люди, смены и правила — только для управляющих.": "People, shifts and rules — for managers only.",
  // ---------------------------------------------------------- общее: нижняя полоса и сайдбар --
  "События": "Events",
  "Табель": "Timesheet",
  // «График» — расписание смен; табель (сводка часов) в сайдбаре больше не отдельный пункт.
  "График": "Schedule",
  "График смен": "Shift schedule",
  "Материалы": "Materials",
  "Календарь": "Calendar",
  "Ссылки": "Links",
  "Профиль": "Profile",
  "Передачи смены": "Shift handovers",
  "Смена": "Shift",
  "Сервис": "Services",
  "Работа": "Work",
  "Руководитель": "Manager",
  "Управление": "Management",
  "Смена и команда": "Shift and team",
  "Разделы приложения": "App sections",
  // ---------------------------------------------------------- общее: рельс сводки --
  "Поручения": "Tasks",
  "Журнал": "Log",
  "открыто": "open",
  "требуют внимания": "need attention",
  "Нет данных": "No data",
  "Не удалось загрузить": "Couldn't load",
  "Пунктов на сегодня нет": "Nothing scheduled for today",
  "Быстрый переход": "Quick links",
  // ---------------------------------------------------------- общее: ошибка и прогресс --
  "Что-то пошло не так": "Something went wrong",
  "Повторить": "Retry",
  "{done} из {total}": "{done} of {total}",
  // ---------------------------------------------------------- общее: фото и просмотр --
  "Прикрепить фото": "Attach a photo",
  "Сделать фото": "Take a photo",
  "Выбрать фото из галереи": "Choose a photo from the gallery",
  "Галерея": "Gallery",
  "Из галереи": "From gallery",
  "Фото не открылось": "The photo didn't load",
  "Открыть фото": "Open photo",
  "Просмотр фото": "Photo viewer",
  "Закрыть просмотр": "Close viewer",
  "Сбросить масштаб": "Reset zoom",
  "сбросить": "reset",
  "Картинка не открылась — превью недоступно.": "The image didn't load — preview unavailable.",
  "Двойной тап или колесо — зум · тяни, чтобы сдвинуть · тап по фону — закрыть": "Double-tap or scroll to zoom · drag to pan · tap the background to close",
  "Сеанс уже был закрыт.": "The session was already closed.",
  "Вышли из режима проверки. Удалено: задач {tasks}, смен {runs}, проблем {issues}.": "Left review mode. Removed: {tasks} tasks, {runs} shifts, {issues} issues.",
  "Выйти": "Exit",
  "Чистим…": "Cleaning up…",
  // ---------------------------------------------------------- общее: календарные подписи --
  "свободен": "free",
  "может": "maybe",
  "занят": "busy",
  "своб.": "free",
  "зан.": "busy",
  "Часы": "Hours",
  "День": "Day",
  "Неделя": "Week",
  "Месяц": "Month",
  "3 мес": "3 mo",
  "Полгода": "6 mo",
  "Год": "Year",
  "Все годы": "All years",
  // ---------------------------------------------------------- календарь: навигация и подсказки --
  "к сегодня": "to today",
  "назад": "back",
  "вперёд": "forward",
  "Вперёд": "Forward",
  "детальнее": "zoom in",
  "общий вид": "overview",
  "Управление календарём": "Calendar controls",
  "Зум таймлайна": "Timeline zoom",
  "Авто-ширина по контейнеру": "Auto width by container",
  "Все года": "All years",
  "Загрузка…": "Loading…",
  // ---------------------------------------------------------- календарь: статусы и уверенность --
  "Свободен": "Free",
  "Может": "Maybe",
  "Занят": "Busy",
  "Уверенность": "Confidence",
  "Точно нет": "Definitely not",
  "Скорее нет": "Probably not",
  "Под вопросом": "Unsure",
  "Скорее да": "Probably yes",
  "Точно да": "Definitely yes",
  "Едва ли": "Unlikely",
  "Слабо": "Weak",
  "Склоняюсь": "Leaning yes",
  "Почти точно": "Almost certain",
  "Конкретное время": "Specific time",
  "Не отмечено — считается занятым": "Not marked — counted as busy",
  " (часть дня)": " (part of the day)",
  " · часть дня": " · part of the day",
  "{status}, уверенность {level}/5{partial}": "{status}, confidence {level}/5{partial}",
  "{status} · уверенность {level}/5{partial}": "{status} · confidence {level}/5{partial}",
  // ---------------------------------------------------------- календарь: дни рождения --
  "День рождения {name}": "Birthday {name}",
  "День рождения {names}": "Birthday {names}",
  "День рождения: {name}": "Birthday: {name}",
  "День рождения: {names}": "Birthdays: {names}",
  "Креативное поздравление": "Creative greeting",
  "Ещё вариант": "Another option",
  "Скопировано в буфер.": "Copied to the clipboard.",
  "Не получилось скопировать — выдели текст вручную.": "Couldn't copy — select the text manually.",
  "Пост от лица бота": "Post as the bot",
  "Пост от своего имени": "Post as yourself",
  "Бот запостит этот текст в чат и подпишет, что поздравил — ты. Отправляем?": "The bot will post this text to the chat and sign it as you. Send it?",
  "Бот запостит этот текст в чат от своего лица. Отправляем?": "The bot will post this text under its own name. Send it?",
  "Запостил в чат от лица бота.": "Posted to the chat as the bot.",
  "Запостил в чат с подписью от тебя.": "Posted to the chat signed as you.",
  "Собираемся на ДР {name}?": "Gathering for {name}'s birthday?",
  "Назначить встречу": "Schedule a meeting",
  // ---------------------------------------------------------- встречи и опросы --
  "Встречи": "Meetings",
  "Опросы": "Polls",
  "Приду": "I'm in",
  "Нет": "No",
  "идёт": "going",
  "нет": "no",
  "Отменить встречу": "Cancel the meeting",
  "Отменить встречу?": "Cancel the meeting?",
  "Запланированных встреч пока нет.": "No meetings scheduled yet.",
  "Открой «Календарь» и запусти авто-подбор лучших слотов.": "Open the Calendar and run auto-pick for the best slots.",
  "Подписаться в системный календарь": "Subscribe in your calendar app",
  "Опросов пока не было.": "No polls yet.",
  "Запусти из «Календаря»: авто-подбор → опрос — бот опубликует его в чате.": "Start it from the Calendar: auto-pick → poll — the bot posts it in the chat.",
  "закрыт": "closed",
  "до {when}": "until {when}",
  "Голосуй прямо в Telegram-чате — результат подтянется сюда.": "Vote in the Telegram chat — the result syncs here.",
  "Твой голос учтён.": "Your vote is counted.",
  // ---------------------------------------- календарь: кнопки плашки --
  "авто": "auto",
  "К сегодня": "To today",
  // ----------------------------------------------- админка: разделы --
  "Вид ленты": "Feed view",
  "Компактный вид": "Compact view",
  "Компактный (новый) / классический": "Compact (new) / classic",
  "Под событием смены — миниатюры участников и бейдж «+XP». Плотнее по вертикали и без отдельных блоков на каждую строку. Выключено — классический вид с карточкой на событие.": "Under a shift event — participant thumbnails and a «+XP» badge. Tighter vertically, no separate block per row. Off — the classic view with a card per event.",
  "Включён": "On",
  "загрузка…": "loading…",
  "Влияет на ленту у всех участников. Откат — переключить тумблер обратно.": "Affects everyone's feed. To undo, flip the switch back.",
  "Интервалы": "Intervals",
  "Как часто проверять очередь напоминаний": "How often to check the reminder queue",
  "мин (1–360)": "min (1–360)",
  "Раз в N минут бот проверяет, кому пора напомнить о встрече.": "Every N minutes the bot checks who needs a meeting reminder.",
  "Напоминания о сроках поручений настраиваются в «Уведомлениях сотрудников»: там же можно выключить конкретное, перенести его вперёд или замьютить все алерты.": "Task deadline reminders are configured in Staff notifications: there you can turn one off, move it earlier or mute all alerts.",
  "Праздники": "Holidays",
  "В эти дни всем участникам капает +50 XP, а бот поздравляет в чате": "Everyone gets +50 XP on these days and the bot posts a greeting in the chat",
  "Не удалось загрузить список — попробуй позже.": "Couldn't load the list — try again later.",
  "Пока ни одного праздника.": "No holidays yet.",
  "+ Добавить праздник": "+ Add holiday",
  "Удалить праздник": "Delete holiday",
  "ДД": "DD",
  "ММ": "MM",
  "Что за праздник": "Which holiday",
  "Пока пусто: поручений ещё не было.": "Empty for now: no tasks yet.",
  "Зачесть": "Credit",
  "Зачесть поручение «{task}» выполненным за {name}?": "Mark task «{task}» as done for {name}?",
  "· автоматически": "· automatic",
  "Система": "System",
  "сотрудника": "the employee",
  "Журнал событий": "Event log",
  "Просроченные поручения и открытые проблемы": "Overdue tasks and open issues",
  "Всё под контролем": "Everything under control",
  "Просроченных поручений нет, открытых проблем нет.": "No overdue tasks, no open issues.",
  "Открыть →": "Open →",
  "Просрочено: {count}": "Overdue: {count}",
  "Осталось {left} из {total}": "{left} of {total} left",
  "Проблемы: {count}": "Issues: {count}",
  "срочных: {count}": "urgent: {count}",
  "Меняй формулировки под свой ресторан. Пустое поле — вернуть исходный текст. Кнопки и юридически значимые подписи здесь не редактируются: их нельзя сломать случайной правкой.": "Rewrite the wording for your restaurant. An empty field restores the original text. Buttons and legally significant labels can't be edited here: a typo can't break them.",
  "Как было": "As it was",
  "Сохранить тексты": "Save texts",
  "Сохранено": "Saved",
  "по умолчанию": "by default",
  "Тексты сохранены.": "Texts saved.",
  "Вид календаря": "Calendar view",
  "Новый таймлайн / legacy-вид": "New timeline / legacy view",
  "Новый таймлайн-вид": "New timeline view",
  "Горизонтальная лента с pinch-zoom и инерционной прокруткой. Активна при увеличениях": "Horizontal strip with pinch-zoom and inertial scrolling. Active at",
  "день / неделя / месяц": "day / week / month",
  "Для остальных уровней (часы, 3/6 месяцев, год, все годы) используется legacy-вид независимо от этого тумблера.": "Other levels (hours, 3/6 months, year, all years) use the legacy view regardless of this switch.",
  "Откат на legacy — переключить тумблер обратно. Никаких рестартов не требуется.": "To go back to legacy, flip the switch back. No restarts needed.",
  "Дни рождения и праздники в одном месте": "Birthdays and holidays in one place",
  "Все ДР-напоминания": "All birthday reminders",
  "Напоминания отключены": "Reminders off",
  "Глобальный рубильник — гасит все ДР-уведомления у всех участников. Персональные галочки ниже при этом сохраняются.": "Master switch — turns off all birthday notifications for everyone. Personal checkboxes below stay as they are.",
  "Дата рождения": "Date of birth",
  "Пресеты времени": "Time presets",
  "слот": "slot",
  "дефолт": "default",
  "Проверь HH:MM": "Check HH:MM",
  "Что показывать при создании опроса / авто-подборе": "What to show when creating a poll / auto-picking",
  "Опрос": "Poll",
  "Авто-подбор": "Auto-pick",
  "За неделю — намёк задать встречу": "A week ahead — a hint to schedule a meeting",
  "В сам день (поздравление)": "On the day (greeting)",
  "За день": "Per day",
  "За месяц": "Per month",
  "Кнопки действий — развернуть": "Action buttons — expand",
  "Свернуть кнопки действий": "Collapse action buttons",
  "свернуть кнопки": "collapse buttons",
  "Настройки": "Settings",
  "Расписание напоминаний": "Reminder schedule",
  "Тик напоминаний": "Reminder tick",
  "Интервал напоминаний, минут": "Reminder interval, minutes",
  "Поручения: кто что сделал и что отправил планировщик": "Tasks: who did what and what the scheduler sent",
  "Подписи, которые видит сотрудник": "Labels employees see",
  "Тексты приложения": "App texts",
  "админка: зачтено вручную": "admin: credited manually",
  "без отдела": "no department",
  "год известен": "year known",
  // ---------------------------------------------- отдел, приватность, мастер --
  "Закрыть подразделение": "Close department",
  "Сотрудников: {count}": "Employees: {count}",
  "Кто сегодня в смене": "Who is on shift today",
  "В отделе пока никого": "Nobody in this department yet",
  "Сотрудники появятся здесь, когда их добавят в подразделение.":
    "Employees appear here once they are added to the department.",
  "Ты видишь состав отдела и статус смены. Подробности — в карточке, если коллега открыл профиль.":
    "You see the department roster and shift status. Details are in the card if your colleague opened their profile.",
  "В смене": "On shift",
  "Сотрудников": "Employees",
  "Просрочки": "Overdue",
  "ты": "you",
  "Вне графика": "Not scheduled",
  "Чек-лист {done}/{total}": "Checklist {done}/{total}",
  "Поручений: {count}": "Tasks: {count}",
  "Вход: {when}": "Last seen: {when}",
  "только что": "just now",
  "{count} мин назад": "{count} min ago",
  "{count} ч назад": "{count} h ago",
  "Последний вход": "Last seen",
  "Скрыто настройками профиля": "Hidden in profile settings",
  "Ещё не заходил(а)": "Hasn't opened the app yet",
  "Последнее медиа": "Latest media",
  "Открыть последнее фото": "Open the latest photo",
  "Фотоотчётов пока нет": "No photo reports yet",
  "Открыть подразделение {name}": "Open department {name}",
  "Так решили": "Set by manager",
  "Последний заход": "Last visit",
  "Коллеги видят, когда ты последний раз открывал приложение.":
    "Colleagues see when you last opened the app.",
  "Включено руководителем — выключить нельзя.":
    "Turned on by your manager — cannot be disabled.",
  "Коллеги видят твоё последнее фото из отчёта.":
    "Colleagues see your latest photo from a report.",
  "Настройка организации": "Company setup",
  "Разделы, подразделения и кто что видит":
    "Sections, departments and who sees what",
  "Видно": "Visible",
  "Скрыто": "Hidden",
  "Предприятие": "Business",
  "Разделы": "Sections",
  "Кто что видит": "Who sees what",
  "Настроим под вас": "Let's set it up for you",
  "Шаг {step} из {total}: {title}": "Step {step} of {total}: {title}",
  "Пропустить": "Skip",
  "Что у вас за предприятие?": "What kind of business is this?",
  "От этого зависят подсказки по подразделениям и акценты в приложении.":
    "This decides department suggestions and what the app emphasises.",
  "Сколько человек в штате?": "How many people work here?",
  "Из кого состоит команда?": "Who is on the team?",
  "Выберите отделы — они появятся в приложении. Свои можно добавить позже в админке.":
    "Pick the departments — they will appear in the app. You can add your own later in admin.",
  "Какие разделы оставить?": "Which sections should stay?",
  "События, задачи, чек-лист и профиль остаются всегда — это ядро смены.":
    "Events, tasks, checklist and profile always stay — they are the core of the shift.",
  "Правила действуют сразу и для всех: их проверяет сервер, а не только интерфейс.":
    "The rules apply immediately to everyone: the server enforces them, not just the interface.",
  "Наблюдение": "Oversight",
  "Руководители видят другие отделы": "Managers see other departments",
  "Выключено: только своё подразделение. Управляющий и админ видят всё в любом случае.":
    "Off: own department only. The owner and admin see everything anyway.",
  "Что сотрудник может скрыть": "What an employee may hide",
  "Если выключить — отметка видна всем и скрыть её нельзя.":
    "If disabled, the mark is visible to everyone and cannot be hidden.",
  "Фото из последнего отчёта: видно коллегам, если разрешено.":
    "The photo from the latest report: visible to colleagues when allowed.",
  "Руководитель и администратор видят карточку всегда — иначе им нечем принимать работу.":
    "A manager and an admin always see the card — otherwise they cannot review work.",
  "Всё готово": "All set",
  "Настройку можно поменять позже — в профиле, «Настройка организации».":
    "You can change the setup later from your profile, under Company setup.",
  "Штат": "Headcount",
  "как есть": "as is",
  "только ядро": "core only",
  "Профили": "Profiles",
  "Начать работу": "Start working",
  "Далее": "Next",
  "Да": "Yes",
  "Своё подразделение": "Custom department",
  "Добавить своё подразделение": "Add a custom department",
  "Добавить": "Add",
  "Ресторан": "Restaurant",
  "Кафе или кофейня": "Cafe or coffee shop",
  "Салон красоты": "Beauty salon",
  "Автосервис": "Auto service",
  "Магазин": "Shop",
  "до 10 человек": "up to 10 people",
  "10–30": "10–30",
  "30–100": "30–100",
  "больше 100": "more than 100",
  "Коллеги видят друг друга": "Colleagues see each other",
  "Человек сам решает в профиле, открывать ли активность.":
    "Each person decides in their profile whether to show activity.",
  "Только руководители": "Managers only",
  "Коллеги видят имя и отдел; цифры и история — у руководителя.":
    "Colleagues see name and department; numbers and history stay with the manager.",
  "Всё открыто": "Everything open",
  "Профили открыты всем и не закрываются. Обычно для прозрачной команды.":
    "Profiles are open to everyone and cannot be closed. Usually for a transparent team.",
  "График смен: кто когда работает": "Shift schedule: who works when",
  "Сотрудники, роли и карточки": "Employees, roles and cards",
  "Работы, которые сдал персонал": "Work submitted by the team",
  "Срез дня и выгрузка": "Daily snapshot and export",
  "Журнал сбоев и поломок": "Log of faults and breakages",
  "Что важно знать следующей смене": "What the next shift needs to know",
  "Инструкции и тесты по ним": "Instructions and tests on them",
  "Меню, график, методички": "Menu, schedule, guides",
  "Полезные адреса и документы": "Useful addresses and documents",
  "Встречи и отсутствия": "Meetings and absences",
  "Сборы команды и голосования": "Team gatherings and votes",
  // ------------------------------------ админка: список разделов --
  "Смена и люди": "Shift and people",
  "Опыт и результат": "Experience and results",
  "Тексты": "Texts",
  "Частое": "Frequent",
  "Поиск: поручения, тексты, прокси…": "Search: tasks, texts, proxy…",
  "Поиск по разделам": "Search sections",
  "Найдено: {count}": "Found: {count}",
  "Ничего не нашли — попробуй другое слово.":
    "Nothing found — try another word.",
  "Сбросить поиск": "Clear search",
  "Уведомления сотрудников": "Staff notifications",
  "Что ушло и что запланировано; мьют всех алертов":
    "What was sent and what is scheduled; mute all alerts",
  "Пул сотрудников": "Employee pool",
  "Имя, должность, прогресс, увольнение и корзина":
    "Name, position, progress, dismissal and recycle bin",
  "Приглашения": "Invitations",
  "Ссылка с ролью и сроком — вход без Telegram":
    "A link with a role and an expiry — sign-in without Telegram",
  "Пункты по залам, закрытие смены, фотоотчёт":
    "Items per hall, shift closing, photo report",
  "Обучение и ставка": "Training and rate",
  "Учебные и обычные смены по дням, полная/частичная ставка":
    "Training and regular shifts by day, full or partial rate",
  "Создать, назначить, срок с точностью до секунды":
    "Create, assign, set a deadline down to the second",
  "Поручения человеческим языком, включая автоматику":
    "Tasks in plain words, including automation",
  "Компактный (миниатюры участников) / классический":
    "Compact (participant thumbnails) / classic",
  "Опыт и сотрудник недели": "Experience and employee of the week",
  "Сколько стоит работа · рейтинг недели":
    "What work costs · weekly rating",
  "Дни рождения и праздники": "Birthdays and holidays",
  "Даты, поздравления и напоминания": "Dates, greetings and reminders",
  "Слоты для опросов / авто-подбора (12-15, 15-18 …)":
    "Slots for polls and auto-pick (12–15, 15–18 …)",
  "Приветствие, подсказки и пустые состояния":
    "Greeting, hints and empty states",
  "Интервалы напоминаний": "Reminder intervals",
  "Запланированные задачи": "Scheduled jobs",
  "APScheduler-job'ы: изменить, пропустить":
    "APScheduler jobs: edit, skip",
  "Прокси": "Proxy",
  "Пул прокси, индикаторы, авто-фолбэк":
    "Proxy pool, indicators, auto fallback",
  "Рестарт Space": "Space restart",
  "Кнопка + расписание, если сеть зависла":
    "A button and a schedule for when the network hangs",
  // ------------------------- звания, ачивки и счётчики (с сервера) --
  "Стажёр": "Trainee",
  "Ровный ход": "Steady pace",
  "Опора смены": "Shift anchor",
  "Наставник": "Mentor",
  "Мастер смены": "Shift master",
  "до «{rank}» — {xp} XP": "{xp} XP to “{rank}”",
  "Номинант появится, когда кто-то наберёт минимум опыта за неделю ({xp} XP).":
    "A nominee appears once someone earns at least the weekly minimum ({xp} XP).",
  "Первое закрытое поручение": "First completed task",
  "10 закрытых поручений": "10 completed tasks",
  "50 закрытых поручений": "50 completed tasks",
  "100 закрытых поручений": "100 completed tasks",
  "500 закрытых поручений": "500 completed tasks",
  "1000 поручений: легенда смены": "1000 tasks: shift legend",
  "Первая смена в системе": "First shift in the system",
  "10 рабочих дней": "10 workdays",
  "100 рабочих дней": "100 workdays",
  "300 рабочих дней": "300 workdays",
  "1000-й рабочий день": "1000th workday",
  "10 пунктов чек-листа": "10 checklist items",
  "100 пунктов чек-листа": "100 checklist items",
  "1000 пунктов чек-листа": "1000 checklist items",
  // ------------------------------------ залы чек-листа и должности --
  "Первый зал": "First hall",
  "Второй зал": "Second hall",
  "Официант": "Waiter",
  "Разработчик": "Developer",
  "Менеджер": "Manager",
  "Повар": "Cook",
  "Бармен": "Bartender",
  "Администратор зала": "Floor manager",
  "Су-шеф": "Sous-chef",
  "Хостес": "Host",
  // ------------------------------------ тексты, которые правит админ --
  "Ссылка показывается один раз и работает как пароль — не пересылай её. Действует до {date}.":
    "The link is shown once and works like a password — don't forward it. Valid until {date}.",
  "использована {when}": "used {when}",
  "Открыть на компьютере": "Open on a computer",
  "Загляни в события смены.": "Take a look at the shift events.",
  "Фотоотчёт можно снять прямо в пункте с 📷, а «не успеваю» — в карточке задачи. Чат с ботом тоже работает.":
    "You can attach a photo right in a checklist item (📷), and report “running late” from the task card. The bot chat works too.",
  // ------------------------------------------ админка: поручения --
  "Создать и закрыть · срок с точностью до секунды":
    "Create and close · deadline to the second",
  "Новое поручение": "New task",
  "Что сделать": "What to do",
  "Проверить сроки на витрине": "Check expiry dates on the display",
  "Отделу": "To the department",
  "Сотруднику": "To an employee",
  "— выбери —": "— choose —",
  "— загружаем список —": "— loading the list —",
  "Описание (необязательно)": "Description (optional)",
  "Что должно получиться (необязательно)": "Expected result (optional)",
  "Смена (необязательно)": "Shift (optional)",
  "Приоритет": "Priority",
  "Повторяется по дням (необязательно)": "Repeats on weekdays (optional)",
  "В остальные дни поручение остаётся в списке, но помечено как не сегодняшнее.":
    "On other days the task stays in the list, marked as not for today.",
  "Ничего не выбрано — срок работает как обычно.":
    "Nothing selected — the deadline works as usual.",
  "Нужен фотоотчёт": "Photo report required",
  "С приёмкой": "Needs review",
  "Срок — дата, время и секунды. Пусто = без срока.":
    "Deadline — date, time and seconds. Empty means no deadline.",
  "Дата": "Date",
  "Время": "Time",
  "Сек.": "Sec.",
  "Пн": "Mon",
  "Вт": "Tue",
  "Ср": "Wed",
  "Чт": "Thu",
  "Пт": "Fri",
  "Сб": "Sat",
  "Вс": "Sun",
  "понедельник": "Monday",
  "вторник": "Tuesday",
  "среда": "Wednesday",
  "четверг": "Thursday",
  "пятница": "Friday",
  "суббота": "Saturday",
  "воскресенье": "Sunday",
  "+30 мин": "+30 min",
  "+1 час": "+1 hr",
  "до конца смены": "until end of shift",
  "завтра": "tomorrow",
  "Срок: {when}": "Deadline: {when}",
  "Срок не задан — планировщик не будет напоминать.":
    "No deadline set — the scheduler won't remind anyone.",
  "Создать поручение": "Create task",
  "Создаю…": "Creating…",
  "Поручение создано — сотрудник увидит его в приложении.":
    "Task created — the employee will see it in the app.",
  "Последние поручения": "Latest tasks",
  " · фото {count}": " · {count} photo(s)",
  "Зачесть выполненным": "Mark as done",
  "Закрыть «{title}»?": "Close “{title}”?",
  "Пока пусто — создай первое поручение выше.":
    "Empty so far — create the first task above.",
  "Напиши, что нужно сделать.": "Describe what needs to be done.",
  "Выбери подразделение.": "Choose a department.",
  "Выбери сотрудника.": "Choose an employee.",
  "Укажи время дедлайна или очисти дату.":
    "Set a deadline time or clear the date.",
  // ---------------------------------------- админка: сотрудники --
  "Переименовать, должность, прогресс, увольнение":
    "Rename, position, progress, dismissal",
  "Найти по имени, должности или отделу":
    "Search by name, position or department",
  "Имя": "Name",
  "Должность (влияет на пункты чек-листа)":
    "Position (affects checklist items)",
  "Плашка под именем": "Badge under the name",
  "например, Управляющий": "for example, Owner",
  "например, су-шеф": "for example, sous-chef",
  "Роль в боте": "Role in the bot",
  "Обнулить прогресс": "Reset progress",
  "Обнулить знакомство с приложением у {name}? Уроки и онбординг сбросятся, отдел и опыт останутся.":
    "Reset the app onboarding for {name}? Lessons and onboarding restart; department and experience stay.",
  "Прогресс знакомства обнулён.": "Onboarding progress reset.",
  "Уволить": "Dismiss",
  "Уволить {name}? Доступ в приложение и к боту закроется сразу.":
    "Dismiss {name}? Access to the app and the bot closes immediately.",
  "Что делать с данными? OK — в корзину на {days} дней, отмена — удалить сразу и бесповоротно.":
    "What should happen to the data? OK — the bin for {days} days; cancel — delete immediately and for good.",
  "Ещё раз: данные уедут в корзину и удалятся сами через {days} дней. Продолжаем?":
    "Once more: the data goes to the bin and is deleted automatically after {days} days. Continue?",
  "Ещё раз: данные сотрудника будут удалены сразу и бесповоротно. Уверен?":
    "Once more: the employee's data will be deleted immediately and for good. Sure?",
  "Увольнение даёт выбор: корзина на {days} дней или удаление сразу.":
    "Dismissal gives a choice: the bin for {days} days or immediate deletion.",
  "Доступ закрыт.": "Access closed.",
  "Данные в корзине до {date}.": "Data stays in the bin until {date}.",
  "Данные удалены.": "Data deleted.",
  "Вернуть из корзины": "Restore from the bin",
  " · уволен": " · dismissed",
  "Сотрудник возвращён: доступ открыт.":
    "Employee restored: access is open.",
  // ------------------------------------- админка: уведомления --
  "Уведомления": "Notifications",
  "Что ушло и что запланировано": "What was sent and what is scheduled",
  "Мьют алертов": "Mute alerts",
  "Алерты в чат идут как обычно.": "Chat alerts are running as usual.",
  "24 ч": "24 h",
  "Снять мьют": "Unmute",
  "На {label}": "For {label}",
  "Чат молчит до {when}.": "The chat stays silent until {when}.",
  "Замьютить все алерты в чат на {label}? В приложении уведомления продолжат копиться.":
    "Mute all chat alerts for {label}? Notifications keep piling up in the app.",
  "Подтверди ещё раз: пока мьют активен, бот молчит про сроки и просрочки.":
    "Confirm once more: while muted, the bot says nothing about deadlines and overdue tasks.",
  "Мьют глушит доставку в Telegram: в приложении уведомления продолжают копиться, поэтому утренний разбор не потеряется.":
    "Mute silences Telegram delivery only: notifications keep piling up in the app, so the morning review is not lost.",
  "Написать сотруднику": "Message an employee",
  "Кому отправить уведомление": "Who gets the notification",
  "Что сказать сотруднику": "What to tell the employee",
  "Личное уведомление в приложение. В чат не дублируем.":
    "A personal notification in the app. Not duplicated to the chat.",
  "Запланировано ({count})": "Scheduled ({count})",
  "Отложенных напоминаний нет: у открытых поручений не проставлен срок.":
    "No delayed reminders: open tasks have no deadline set.",
  "перенесено вручную": "moved manually",
  "напоминание о сроке": "deadline reminder",
  "вторая ступень": "second stage",
  "Передвинуть": "Move",
  "Выключить": "Turn off",
  "Выключить напоминание по «{title}»? Вернуть можно здесь же, в «Выключено».":
    "Turn off the reminder for “{title}”? You can bring it back here, under Turned off.",
  "Выключено ({count})": "Turned off ({count})",
  "Напоминание о сроке не уйдёт. Вторая ступень после просрочки работает отдельно.":
    "The deadline reminder will not be sent. The second stage after the deadline works separately.",
  "Отправлено ({count})": "Sent ({count})",
  "Колокольчики пока пустые.": "No bell notifications yet.",
  "· не прочитано": "· unread",
  "Убрать": "Remove",
  "Убрать это уведомление из колокольчика сотрудника?":
    "Remove this notification from the employee's bell?",
  "без профиля": "no profile",
  "+15 мин": "+15 min",
  "+1 ч": "+1 hr",
  "+3 ч": "+3 hr",
  // ------------------------------------- админка: приглашения --
  "Ссылка с ролью и сроком": "A link with a role and an expiry",
  "Ссылка открывает вход в организацию. Если указать почту, принять сможет только её владелец; без почты ссылку можно скинуть в чат смены. Ссылка одноразовая и перестаёт работать после присоединения.":
    "The link opens access to the company. With an email, only its owner can accept; without one, share the link in the shift chat. The link is single-use and stops working after joining.",
  "Роль приглашённого": "Invitee role",
  "Роль": "Role",
  "Срок": "Expiry",
  "Срок приглашения": "Invitation expiry",
  "Почта (необязательно)": "Email (optional)",
  "Создать ссылку": "Create link",
  "Создаём…": "Creating…",
  "Новая ссылка": "New link",
  "Не получилось скопировать — выдели ссылку вручную.":
    "Couldn't copy — select the link manually.",
  "Письмо ушло на {email}.": "The email went to {email}.",
  "Письмо не отправлено — почтовый транспорт на сервере не настроен. Скопируй ссылку и отправь вручную.":
    "The email was not sent — mail transport is not configured on the server. Copy the link and send it yourself.",
  "Почты нет — отправь ссылку в чат смены.":
    "No email — send the link to the shift chat.",
  "Живые ссылки ({count})": "Active links ({count})",
  "до {date}": "until {date}",
  "{value} дн.": "{value} days",
  "для всех, у кого есть ссылка": "for anyone with the link",
  "Отозвать ссылку? Она перестанет работать сразу.":
    "Revoke the link? It stops working immediately.",
  "Ссылок нет. Создай первую — она появится здесь до присоединения или отзыва.":
    "No links yet. Create the first one — it shows up here until someone joins or you revoke it.",
  // ------------------------------------------ админка: KPI смены --
  "Опыт за работу, рейтинг недели": "Experience for work, weekly rating",
  "Сколько стоит работа": "What work is worth",
  "КПИ смены": "Shift KPI",
  "KPI смены": "Shift KPI",
  "За каждый отмеченный пункт смены": "For each ticked shift item",
  "Закрытая смена": "Closed shift",
  "Когда все обязательные пункты закрыты":
    "When all required items are ticked",
  "За закрытое поручение АУП": "For a completed management task",
  "Надбавка, если поручение закрыто до дедлайна":
    "Bonus when a task is closed before its deadline",
  "В срок": "On time",
  "Минимум для звания": "Minimum for a rank",
  "Сколько опыта нужно за неделю, чтобы стать сотрудником недели":
    "How much weekly experience makes someone employee of the week",
  "Веса действуют сразу: опыт пересчитывается из уже записанных смен и поручений, передеплой не нужен.":
    "Weights apply immediately: experience is recalculated from existing shifts and tasks, no redeploy needed.",
  "За эту неделю ещё нет закрытых смен и поручений.":
    "No shifts or tasks closed this week yet.",
  "Пока никто не набрал минимум опыта — звание свободно. Это нормально в начале недели.":
    "Nobody has reached the minimum yet — the title is open. That is normal early in the week.",
  "Тестовый пул `[BOT]` в рейтинг не попадает: звание — для живых людей.":
    "The `[BOT]` test pool never appears in the rating: the title is for real people.",
  "{from} — {to} · Mon–Sun": "{from} — {to} · Mon–Sun",
  "{count} tasks": "{count} tasks",
  // -------------------------------------- админка: рестарт Space --
  "Принудительный перезапуск backend-контейнера на HF":
    "Forced restart of the backend container on HF",
  "Лечит зависшие сетевые состояния (дохлый keep-alive-пул). Mini App переключается бесшовно; бот восстанавливает вебхук ~5–7 минут.":
    "Heals stuck network states (a dead keep-alive pool). The mini app switches seamlessly; the bot restores its webhook in about 5–7 minutes.",
  "Анти-луп: не чаще раза в 30 минут (после ручного — тоже).":
    "Anti-loop: at most once every 30 minutes (after a manual restart too).",
  "Один раз": "Once",
  "По расписанию": "On schedule",
  "Сейчас": "Now",
  "Выкл": "Off",
  "Каждые N ч": "Every N hours",
  "Дата и время (локальные)": "Date and time (local)",
  "Сохранить расписание": "Save schedule",
  "1 ч … 168 ч (неделя). Отсчёт — от последнего рестарта.":
    "1 h … 168 h (a week). Counted from the last restart.",
  "Каждые {hours} ч": "Every {hours} h",
  "Последний рестарт: {when}": "Last restart: {when}",
  "Следующий рестарт: {when}": "Next restart: {when}",
  "Перезапустить Space? Бот будет молчать ~5–7 минут.":
    "Restart the Space? The bot will be silent for about 5–7 minutes.",
  "Space перезапускается. Mini App почти не заметит, но бот будет ":
    "The Space is restarting. The mini app barely notices, but the bot will be ",
  "молчать ~5–7 минут, пока вебхук переустановится.":
    "silent for about 5–7 minutes while the webhook is reinstalled.",
  "не задан в настройках Space — рестарт недоступен. Добавь write-токен HF в секреты Space.":
    "is not set in the Space settings — restart is unavailable. Add an HF write token to the Space secrets.",
  // ------------------------------------- админка: обучение и ставка --
  "Учебные и обычные смены, ставка сотрудника":
    "Training and regular shifts, employee rate",
  "Ближайшие две недели": "Next two weeks",
  "Ставка": "Rate",
  "Полная ставка": "Full rate",
  "Частичная ставка": "Partial rate",
  "Учебных смен по табелю": "Training shifts per timesheet",
  "Больше учебных смен": "More training shifts",
  "Меньше учебных смен": "Fewer training shifts",
  "Вернуть правило": "Restore rule",
  "Учебные смены считаются по табелю: первые N отработанных смен. Больничный или пропуск учебную не съедает, перенесённая смена остаётся в счёте. Пока табель пуст, работает старый расчёт по дате найма. Любой день можно переключить вручную.":
    "Training shifts are counted from the timesheet: the first N worked shifts. Sick leave or a missed day does not consume one, and a moved shift still counts. While the timesheet is empty, the old hire-date calculation applies. Any day can be switched manually.",
  "Обычная смена — нажми, чтобы сделать учебной":
    "Regular shift — tap to make it a training one",
  "Учебная смена — нажми, чтобы сделать обычной":
    "Training shift — tap to make it regular",
  "Синее — учебная смена, серое — обычная. Точка — день задан вручную, буква — статус в табеле (Р работа, П план, Б больничный, Н не пришёл, В выходной, О отпуск).":
    "Blue is a training shift, grey a regular one. A dot marks a manually set day; the letter is the timesheet status (W worked, P planned, S sick, A absent, D day off, V vacation).",
  "табель пуст · с {date}": "timesheet empty · since {date}",
  "табель пуст · включено «учимся вместе»":
    "timesheet empty · learning together is on",
  "считаем отработанные смены": "counting worked shifts",
  "Без должности": "No position",
  "Сотрудников в твоём подразделении нет.":
    "There are no employees in your department.",
  "· учебных {training} из {total}": "· {training} of {total} training",
  "· {count} вручную": "· {count} manual",
  "· табель пуст": "· timesheet empty",
  "учебная": "training",
  "обычная": "regular",
  "П": "P",
  "Р": "W",
  "Б": "S",
  "Н": "A",
  "В": "D",
  "О": "V",
  // ------------------------------------ админка: снапшоты состава --
  "Снапшоты состава": "Roster snapshots",
  "Сотрудник видит только свои действия, руководитель — своё подразделение, администратор — всё и получает кабинет.":
    "An employee sees only their own actions, a manager sees their department, an admin sees everything and gets the console.",
  "Добавить в бот": "Add to the bot",
  "Добавить по Telegram ID": "Add by Telegram ID",
  "как показывать в боте": "how to show it in the bot",
  "Должность в боте (роль)": "Position in the bot (role)",
  "Имя (необязательно)": "Name (optional)",
  "Должность (необязательно)": "Position (optional)",
  "Подразделение (необязательно)": "Department (optional)",
  "например, 123456789": "for example, 123456789",
  "Добавляем…": "Adding…",
  "Ищем…": "Searching…",
  "Найти по ID": "Find by ID",
  "без подразделения": "no department",
  "«Найти по ID» открывает карточку того, кто уже в пуле: там же можно сменить роль. Существующий профиль не перезаписывается.":
    "“Find by ID” opens the card of someone already in the pool: the role can be changed there too. An existing profile is not overwritten.",
  "Такого Telegram-ID в пуле нет. Проверь цифры или добавь человека — ID показывает команда /whoami.":
    "No such Telegram ID in the pool. Check the digits or add the person — /whoami shows the ID.",
  "{name} заведён(а) в боте · {role}.": "{name} added to the bot · {role}.",
  "{name} уже в пуле · {role}.": "{name} is already in the pool · {role}.",
  "Снимок {when}": "Snapshot {when}",
  "Снять снапшот сейчас": "Take a snapshot now",
  "Снимков пока нет.": "No snapshots yet.",
  "Снимок готов: людей в составе — {count}.":
    "Snapshot ready: {count} people in the roster.",
  "Заведено заново: {count}": "Created anew: {count}",
  "Возвращено из корзины: {count}": "Restored from the bin: {count}",
  "Не тронуто (уже есть): {count}": "Untouched (already exist): {count}",
  "Нет отделов: {names}": "Missing departments: {names}",
  "Вернуть людей из снимка «{label}» от {when}? Активных сотрудников не трогаем — вернутся только пропавшие.":
    "Bring people back from the snapshot “{label}” of {when}? Active employees stay untouched — only missing ones return.",
  "Удалить снимок от {when}? Восстановить по нему будет нельзя.":
    "Delete the snapshot of {when}? It cannot be used to restore afterwards.",
  "Восстановить": "Restore",
  "Без названия": "Untitled",
  "без названия": "untitled",
  " · авто": " · auto",
  "Перед удалением сотрудника или очисткой тестового пула снимок снимается сам. Храним последние {count}.":
    "A snapshot is taken automatically before deleting an employee or clearing the test pool. We keep the latest {count}.",
  // -------------------------------------- админка: очередь задач --
  "Очередь задач": "Job queue",
  "APScheduler-job'ы + напоминания встреч":
    "APScheduler jobs + meeting reminders",
  "Очередь": "Queue",
  "Обновить и опрашивать чаще 3 минуты": "Refresh and poll more often than 3 minutes",
  "тик 5с": "tick 5s",
  "тик 10м": "tick 10m",
  "— подвинуть время ближайшего запуска.":
    "— move the time of the next run.",
  "— пропустить ближайший запуск (recurring остаётся, one-shot/reminder удаляются).":
    "— skip the next run (recurring stays, one-shot/reminder jobs are deleted).",
  "Схлопнуть все запланированные авто-задачи":
    "Collapse all scheduled auto-jobs",
  "Схлопнуть ВСЕ запланированные авто-задачи? Планировщик пересоберётся с нуля, как после рестарта.":
    "Collapse ALL scheduled auto-jobs? The scheduler rebuilds from scratch, as after a restart.",
  "Пересобираем…": "Rebuilding…",
  "Планировщик пересобран: {count} задач.":
    "Scheduler rebuilt: {count} jobs.",
  "Очередь пуста.": "The queue is empty.",
  "Изменить время": "Change time",
  "Сохранить время": "Save time",
  "Некорректное время": "Invalid time",
  "Пропустить ближайший запуск": "Skip the next run",
  "Удалить «{label}»?": "Delete “{label}”?",
  "Пропустить ближайший запуск «{label}»?":
    "Skip the next run of “{label}”?",
  // ----------------------------------- админка: чек-листы и залы --
  "Пункты дня, залы и фотоотчёты":
    "Daily items, halls and photo reports",
  "Вид чек-листа": "Checklist type",
  "Название чек-листа": "Checklist name",
  "Залы ресторана": "Restaurant halls",
  "Название нового зала": "New hall name",
  "Название зала {name}": "Hall name {name}",
  "Создать чек-лист": "Create checklist",
  "Чек-листа ещё нет": "No checklist yet",
  "Зал": "Hall",
  "Общий": "General",
  "Общий (все залы)": "General (all halls)",
  "Открытие — {hall}": "Opening — {hall}",
  "Закрытие смены": "Shift closing",
  "Пункт": "Item",
  "Обязательный пункт": "Required item",
  "Закрывается только фотоотчётом": "Closed only by a photo report",
  "Включён у сотрудников": "Enabled for staff",
  "Вкл": "On",
  "Выключен": "Off",
  "Выше": "Up",
  "Ниже": "Down",
  "Пояснение (необязательно)": "Hint (optional)",
  "В какие дни нужен пункт": "Which days need this item",
  "Ничего не выбрано — каждый день.": "Nothing selected — every day.",
  "{label}. В другие дни пункт виден, но неактивен.":
    "{label}. On other days the item is visible but inactive.",
  "Что сделать? Например: протереть столы 400–500":
    "What to do? For example: wipe tables 400–500",
  "＋ Добавить пункт": "+ Add item",
  "Пока ни одного пункта. Добавь первый — иначе сотрудник увидит пустой экран.":
    "No items yet. Add the first one or staff will see an empty screen.",
  "Удалить пункт «{title}»? Отметки в уже пройденных сменах исчезнут.":
    "Delete the item “{title}”? Ticks in past shifts disappear.",
  "{hall} · {kind}. Создай шаблон и добавь пункты — они сразу появятся у сотрудников.":
    "{hall} · {kind}. Create a template and add items — staff see them right away.",
  "После заведения зала создай для него чек-лист открытия: выбери зал выше и нажми «Создать чек-лист».":
    "After adding a hall, create its opening checklist: pick the hall above and press “Create checklist”.",
  "«Общий» — шаблон-подстраховка, если для зала своего нет. Закрытие смены всегда общее.":
    "“General” is the fallback template when a hall has none of its own. Shift closing is always shared.",
  "Закрытие смены — общий чек-лист: один на все залы. Последний пункт обычно с фотоотчётом.":
    "Shift closing is a shared checklist: one for all halls. The last item usually needs a photo report.",
  "Чек-лист первого дня показывается новичку; зала у него нет.":
    "The first-day checklist is shown to newcomers; they have no hall.",
  "{count} залов · от них зависит выбор у официанта":
    "{count} halls · the waiter's choice depends on them",
  "{count} пунктов · фотоотчёт обязателен у {photos}":
    "{count} items · photo report required for {photos}",
  "только «{position}»": "only “{position}”",
  // --- Первый запуск: сотрудник или руководитель ---
  "SemiWorkBot": "SemiWorkBot",
  "Кто вы в компании?": "Who are you at the company?",
  "Сотрудник присоединяется к готовой команде по коду от руководителя. Руководитель создаёт организацию и приглашает людей сам.":
    "An employee joins an existing team with a code from their manager. A manager creates the organization and invites people.",
  "Я сотрудник": "I'm an employee",
  "Присоединиться к команде: нужен код компании или её точное название.":
    "Join the team: you'll need the company code or its exact name.",
  "Я руководитель": "I'm a manager",
  "Создать организацию, пригласить сотрудников и настроить отделы.":
    "Create an organization, invite employees and set up departments.",
  "Вход в организацию": "Join an organization",
  "Руководитель видит код компании в разделе «Приглашения». Если кода нет, подойдёт точное название — слово в слово, как в приложении.":
    "Your manager sees the company code in the Invitations section. Without a code, the exact name works — word for word as it appears in the app.",
  "Код или название компании": "Company code or name",
  "ID или название": "ID or name",
  "Проверяем…": "Checking…",
  "Присоединиться": "Join",
  "Если код не подходит, попросите у руководителя ссылку-приглашение — она сразу откроет нужную организацию.":
    "If the code doesn't work, ask your manager for an invitation link — it opens the right organization right away.",
  "Новая организация": "New organization",
  "Вы станете её владельцем. После этого — короткая настройка: отделы и разделы приложения.":
    "You'll become its owner. After that comes a short setup: departments and app sections.",
  "Название компании": "Company name",
  "Например, «Кофейня на углу»": "For example, “The Corner Café”",
  "Направление": "Industry",
  "Создать организацию": "Create organization",
  "Сотрудников можно пригласить сразу после настройки — ссылкой или кодом компании.":
    "You can invite employees right after setup — with a link or the company code.",
  "Выйти из аккаунта": "Sign out",
  "Выходим…": "Signing out…",
  "ID компании": "Company ID",
  "Сотрудник вводит его на первом экране — вместе с названием или вместо него. Список компаний снаружи не виден.":
    "An employee enters it on the first screen — instead of or along with the name. The list of companies is not visible from outside.",
  "Кто в приложении": "Who has access",
  "Роль меняет и доступ в админку: владелец — администратор, управляющий — руководитель. Свою роль изменить нельзя — попроси другого администратора.":
    "The role also changes admin access: an owner becomes an administrator, a manager becomes a supervisor. You can't change your own role — ask another administrator.",
  "это ты": "that's you",
  "без почты": "no email",
  "Роль {name}": "Role of {name}",
  "Например, «Бармен»": "For example, “Bartender”",
  "Должность в приглашении": "Position in the invitation",
  "Отдел (необязательно)": "Department (optional)",
  "Отдел в приглашении": "Department in the invitation",
  "Без отдела": "No department",
  "Вас ждут: {details}": "You'll join as: {details}",
  // Утренняя сводка руководителю (план, 8.2): экран и вход в него из «Команды».
  "Сводка дня": "Daily summary",
  "Обновить": "Refresh",
  "То же, что приходит в 08:30 — без поиска по чату":
    "The same thing that arrives at 08:30 — without scrolling the chat",
  "Всё спокойно": "All calm",
  "Просрочек и срочных проблем нет — можно заниматься сменой.":
    "No overdue items or urgent problems — you can get on with the shift.",
  "Не открыли чек-лист": "Checklist not opened",
  "из {count} смен": "of {count} shifts",
  "чек-листов сейчас": "checklists right now",
  "Что просрочено": "What is overdue",
  "Открыть поручения": "Open tasks",
  "Числа считаются по тем же правилам видимости, что журнал и задачи: чужого здесь не показывается.":
    "The numbers follow the same visibility rules as the journal and tasks: nothing foreign is shown here.",
  // Языковой пакет (план, 8.3): админский экран «Языки» и его тексты.
  "Языки": "Languages",
  "Языковой пакет для команды: включён по умолчанию":
    "Uzbek pack: on by default",
  "Языковой пакет команды": "The team language pack",
  "Узбекский язык": "Uzbek",
  "Переведены рабочие экраны смены: чек-лист и мои задачи. Остальное остаётся русским — непереведённая строка читается, а не превращается в код.":
    "The shift screens are translated: the checklist and my tasks. The rest stays Russian — an untranslated line still reads as text, not as a key.",
  "Влияет на всех: узбекский появляется в переключателе языка у каждого сотрудника. Выключено — в переключателе остаются русский и английский.":
    "Affects everyone: Uzbek appears in every employee's language switcher. When off, the switcher keeps Russian and English.",
  "Решает владелец организации: языковой пакет — часть тарифа, а не настройка смены.":
    "Decided by the organization owner: the language pack is part of the plan, not a shift setting.",
  "Менять может только владелец организации: языковой пакет — часть тарифа. Попроси его включить перевод.":
    "Only the organization owner can change this: the language pack is part of the plan. Ask them to turn the translation on.",
  "Как работает перевод": "How the translation works",
  // Единицы для «4 ч», «3 дня» в экране проблем. Ключи были только
  // русскими: `txIn` их берёт, а страж покрытия видит только `tx(...)`.
  // Появились вместе с узбекским пакетом — и заодно починили английский.
  "день": "day",
  "дня": "days",
  "дней": "days",
  "ч": "h",
  "меньше часа": "less than an hour",
  "Сотрудник выбирает язык сам кнопками RU / EN / UZ — выбор хранится в его устройстве и переживает перезапуск. Организация решает только, доступен ли узбекский.":
    "Each employee picks the language with the RU / EN / UZ buttons — the choice is stored on their device and survives a restart. The organization only decides whether Uzbek is available.",
  "Русский — оригинал и запасной вариант: если строки нет в пакете, она показывается по-русски. Поэтому пакет можно включать по мере перевода, а не «когда всё готово».":
    "Russian is both the original and the fallback: if a line is missing from the pack, it shows in Russian. That means the pack can be enabled as translation proceeds, not only when everything is done.",
  "Выключили пакет — те, у кого был выбран узбекский, сразу возвращаются на русский: остаться на языке, которого организация больше не обещает, выглядит как сбой.":
    "Turn the pack off and anyone who had Uzbek selected returns to Russian right away: staying on a language the organization no longer offers looks like a bug.",

  // ------ «Спасибо коллеге» (план, 8.4): карточка недели и лист ------ 
  "Спасибо на этой неделе":
    "Thanks this week",
  "Ты на {place} месте":
    "You're #{place}",
  "Получено":
    "Received",
  "Сказал(а)":
    "Given",
  "Осталось сегодня":
    "Left today",
  "{count} спасибо":
    "{count} thanks",
  "Пока некого благодарить — коллег в твоём подразделении нет.":
    "Nobody to thank yet — there are no colleagues in your department.",
  "Сказать спасибо":
    "Say thanks",
  "Кому сказать спасибо":
    "Who to thank",
  "Поиск по имени":
    "Search by name",
  "Никого не нашли":
    "No one found",
  "Что сделал(а) — необязательно":
    "What they did — optional",
  "Спасибо за смену":
    "Thanks for the shift",
  "Не удалось сказать спасибо.":
    "The thanks didn't send.",
  "Благодарности сейчас не загрузились — остальное в ленте работает.":
    "Thanks didn't load right now — the rest of the feed still works.",
  "Себе спасибо не говорят — выбери коллегу.":
    "You can't thank yourself — pick a colleague.",
  "Этому человеку сказать спасибо нельзя: он не в твоей команде.":
    "You can't thank this person: they aren't in your team.",
  "Сегодня ты уже благодарил(а) этого человека — завтра можно снова.":
    "You already thanked this person today — you can do it again tomorrow.",
  "На сегодня лимит благодарностей исчерпан. Вернись к этому завтра.":
    "Today's limit of thanks is used up. Come back tomorrow.",
  "Слишком длинная запись — хватит одной фразы.":
    "The note is too long — one sentence is enough.",
  "Поблагодарил(а) коллегу":
    "Thanked a colleague",

  // Общий отказ, когда сервер назвал код, которого клиент не знает.
  "Не удалось сказать спасибо. Попробуй ещё раз.":
    "The thanks didn't send. Try again.",

  // План, 8.5: «Сказал — стало поручением».
  "Сказать, что нужно сделать — станет поручением":
    "Say what needs doing — it becomes a task",
  "Скажи, что нужно сделать": "Say what needs doing",
  "Что нужно сделать": "What needs doing",
  "Например: Аня, протри витрину до 18:00":
    "For example: Anna, wipe the display by 18:00",
  "Напиши или продиктуй одной фразой: что сделать, кому и к когда. Срок и исполнителя разберём сами.":
    "Write or dictate one sentence: what to do, to whom, and by when. We'll work out the deadline and the assignee.",
  "Голос распознаёт браузер — запись никуда не отправляется.":
    "Your browser does the speech recognition — the audio is never uploaded.",
  "Сказать голосом": "Say it out loud",
  "Остановить запись": "Stop recording",
  "Не расслышал — попробуй ещё раз или напиши словами.":
    "I didn't catch that — try again or type it.",
  "Поручение создано": "Task created",
  "Исполнитель: {name}": "Assignee: {name}",
  "Поручение отправлено отделу.": "The task went to the department.",
  "Ещё одно": "One more",
  "Кому?": "To whom?",
  "Всему отделу": "The whole department",
  "Кто именно:": "Who exactly:",
  "В команде пока никого нет.": "There's nobody in the team yet.",
  "Выбери исполнителя — без него поручение не создать.":
    "Pick an assignee — a task can't be created without one.",
  "У тебя не указано подразделение — выбери человека, кому поручить.":
    "Your department isn't set — pick the person to assign it to.",
  "Без срока": "No deadline",
  "К концу смены": "By the end of the shift",
  "Через час": "In an hour",
  "Завтра": "Tomorrow",
  "Точное время": "Exact time",
  "Срочно": "Urgent",
  "Фото": "Photo",
  "Срок: конец смены": "Deadline: end of the shift",
  "Срок взят из фразы": "Deadline taken from your words",
  "сегодня": "today",

  // ------------------------------------- админка: пул прокси и селфтест --
  "Пул прокси, индикаторы и парсер из @ProxyMTProto":
    "Proxy pool, health indicators and a parser from @ProxyMTProto",
  "Режим": "Mode",
  "Только через прокси": "Proxy only",
  "Direct (без прокси)": "Direct (no proxy)",
  "Auto-fallback (direct → прокси при проблеме)":
    "Auto-fallback (direct → proxy on failure)",
  "Каждый запрос — через прокси. Если все прокси умерли, бот падает и админ получает алёрт.":
    "Every request goes through a proxy. If every proxy dies, the bot stops and the admin gets an alert.",
  "Прокси не используется. Хорошо когда сеть из РФ открыта.":
    "No proxy is used. Good when the network is reachable directly.",
  "Сначала direct. При сетевой ошибке — следующий живой прокси из пула. Самотест раз в 10 мин.":
    "Direct first. On a network error — the next live proxy from the pool. Self-test every 10 minutes.",
  "Уведомлять админа": "Notify the admin",
  "В личку при падении всех прокси. Не чаще 1 раза в час.":
    "A direct message when every proxy is down. At most once an hour.",
  "Последний алёрт:": "Last alert:",
  "Добавить прокси": "Add a proxy",
  "Вставь сюда блок из @ProxyMTProto (можно несколько подряд) или раскрой «Ввести вручную».":
    "Paste a block from @ProxyMTProto here (several in a row is fine) or open “Enter manually”.",
  "Ввести вручную (IP / port / type / secret)":
    "Enter manually (IP / port / type / secret)",
  "Найти живые прокси": "Find live proxies",
  "Скачать публичный список прокси, пингануть и добавить живые":
    "Download the public proxy list, ping it and add the live ones",
  "Пул прокси": "Proxy pool",
  "Проверяем статус бота…": "Checking the bot status…",
  "Бот активен": "Bot is active",
  "Бот недоступен": "Bot is unreachable",
  "режим:": "mode:",
  "· пул:": "· pool:",
  "Пинг всех (медленно)": "Ping all (slow)",
  "Удалить мёртвые": "Remove the dead ones",
  "Сортировать по скорости (свежее last_ok сверху)":
    "Sort by speed (freshest last_ok first)",
  "Пусто. Бот ходит direct.": "Empty. The bot goes direct.",
  "Отправка сообщений": "Message delivery",
  "через": "via",
  ", ~{ms} мс": ", ~{ms} ms",
  "Проверка ещё не запускалась.": "The check hasn't run yet.",
  "ошибка": "error",
  "Последняя ошибка": "Last error",
  "Очистить ошибку": "Clear the error",
  "Очистить": "Clear",
  "Удалить прокси": "Delete the proxy",
  "secret / pass (опц.)": "secret / pass (optional)",
  "Распарсить": "Parse",
  "Сброс": "Reset",
  "Добавить выбранные ({count})": "Add selected ({count})",
  "Найдено: {count}. Снимите галочки, чтобы пропустить.":
    "Found: {count}. Uncheck the ones to skip.",
  "Добавлено: {ok}": "Added: {ok}",
  "Добавлено: {ok}, пропущено: {fail}": "Added: {ok}, skipped: {fail}",
  "Пул переполнен": "The pool is full",
  "Ошибка БД": "Database error",
  "Не прошёл валидацию": "Failed validation",
  "добавлен": "added",
  "обновлён": "updated",
  "{endpoint} {verb} (не пинговали — enabled=false)":
    "{endpoint} {verb} (not pinged — enabled=false)",
  "{endpoint} {verb}, ping {ms} мс": "{endpoint} {verb}, ping {ms} ms",
  "{endpoint} {verb}. MTProto не проверяется по HTTP — оценишь по селфтесту.":
    "{endpoint} {verb}. MTProto can't be checked over HTTP — the self-test will tell.",
  "{endpoint} {verb}, но не отвечает: {error}":
    "{endpoint} {verb}, but it does not respond: {error}",
  "Скрыть": "Hide",
  "Ошибки добавления ({count})": "Add errors ({count})",
  "Очистить ленту ошибок добавления?": "Clear the add-errors log?",

  // ------------------- встречи: авто-подбор времени и опрос в чат ------
  "Авто-подбор времени": "Auto-pick a time",
  "Окно": "Window",
  "2 недели": "2 weeks",
  "Слоты времени:": "Time slots:",
  "Меняются в админке → «Пресеты времени».": "Changed in the admin panel → “Time presets”.",
  "Найти слоты": "Find slots",
  "Нет подходящих слотов. Скажи всем разметить календарь.":
    "No suitable slots. Ask everyone to mark their calendar.",
  "Публикуем…": "Publishing…",
  "Опубликовать опрос топ-3": "Publish a poll with the top 3",
  "Когда соберёмся?": "When shall we meet?",
  "Опрос опубликован в чате — голосуйте.": "Poll published in the chat — go vote.",
  "Слотов меньше двух — расширь окно или подними пилюли.":
    "Fewer than two slots — widen the window or loosen the time pills.",
  "Ошибка: {error}": "Error: {error}",
  "Опрос в чат": "Poll to the chat",
  "Когда собираемся?": "When are we meeting?",
  "Вопрос": "Question",
  "Указать время": "Include a time",
  "Без времени — в опросе будет только день недели и число.":
    "Without a time the poll shows only the weekday and the date.",
  "Варианты дат (2–6) · по умолчанию пт/сб/вс текущей недели":
    "Date options (2–6) · defaults to Fri/Sat/Sun of this week",
  "Убрать вариант": "Remove the option",
  "+ ещё вариант": "+ one more option",
  "Закрепить опрос в чате": "Pin the poll in the chat",
  "Сразу после публикации бот пин-нёт сообщение (без шумного уведомления).":
    "Right after publishing the bot pins the message (no noisy notification).",
  "Закрыть через": "Close after",
  "6 часов": "6 hours",
  "12 часов": "12 hours",
  "24 часа": "24 hours",
  "2 дня": "2 days",
  "Отправить в чат": "Send to the chat",

  // Перевод данных на месте (план, 11): кнопка в углу блока с чужими
  // данными — поручением или пунктом чек-листа.
  "Перевести": "Translate",
  "Показать оригинал": "Show the original",
  "Перевод недоступен": "Translation isn't available here",

  // Две зоны экрана (02.11.2026): вкладки «Сотрудник | Управление» вверху.
  "Зона приложения": "App area",
  "Кабинет руководителя": "Manager's office",

  // Нижняя полоса зоны управления и экран настроек (02.11.2026).
  "Разделы управления": "Management sections",
  "Язык, вид приложения и доступы": "Language, appearance and access",
  "Язык и вид": "Language and appearance",
  "Язык": "Language",
  "Интерфейс без перезагрузки": "Switches without a reload",
  "Тема": "Theme",
  "Светлая или тёмная — по клику": "Light or dark in one tap",
  "Ещё настройки": "More settings",

  // Меню смены: правка в приложении вместо листа Google (02.11.2026).
  "Меню смены": "Shift menu",
  "Меню правит руководитель: в кабинете, раздел «Меню смены».": "The manager edits the menu in the office, under \"Shift menu\".",
  "Меню пока пустое.": "The menu is empty for now.",
  "по запросу": "on request",
  "нет в наличии": "out of stock",
  "Убрать из меню на сегодня": "Hide from the menu for today",
  "Вернуть в меню": "Put back on the menu",
  "Изменить позицию": "Edit the item",
  "Удалить позицию": "Delete the item",
  "Новая позиция": "New item",
  "Позиция меню": "Menu item",
  "Название, цена и группа — остальное по желанию": "Name, price and group; the rest is optional",
  "Скрыто позиций: {count}": "Hidden items: {count}",
  "Что сегодня в меню: сотрудник видит это в материалах смены": "What is on the menu today: staff see it in shift materials",
  "+ Добавить позицию": "+ Add an item",
  "Например: Борщ с пампушкой": "For example: Borscht with a bun",
  "Группа": "Group",
  "Пусто — общий список «Меню»": "Empty means the single \"Menu\" list",
  "Супы, Горячее, Бар": "Soups, Hot dishes, Bar",
  "Цена, ₽": "Price, RUB",
  "Оставь пустой — будет «по запросу»": "Leave empty to show \"on request\"",
  "Описание": "Description",
  "Состав или подача — по желанию": "Ingredients or serving, optional",
  "Сохраняю…": "Saving…",
  "Позиция скрыта из меню сотрудников.": "The item is hidden from the staff menu.",
  "Удалить «{name}» из меню? Вернуть её можно будет только заново.": "Delete \"{name}\" from the menu? It can only be recreated from scratch.",
  "Пакет отрасли": "Industry pack",
  "Термины, чек-листы, KPI и должности": "Terms, checklists, KPIs and positions",
  "Термины, KPI и должности под ваш бизнес": "Terms, KPIs and positions for your business",
  "Объект работы": "Work object",
  "Рабочий день": "Working day",
  "один": "one",
  "{n} пунктов": "{n} items",
  "Должности": "Positions",
  "Чек-листы": "Checklists",
  "Веса опыта": "Experience weights",
  "Опыт за пункт чек-листа": "Experience per checklist item",
  "Опыт за закрытую смену": "Experience per closed shift",
  "Опыт за закрытое поручение": "Experience per closed assignment",
  "Надбавка за срок": "Bonus for on-time",
  "Минимум для «сотрудника недели»": "Minimum for \"employee of the week\"",
  "Что изменится": "What will change",
  "Пакет добавляет то, чего ещё нет: слова, пару чек-листов на каждое подразделение и подсказки должностей. Переименованное и отредактированное он не трогает.": "The pack adds what is still missing: words, an opening and closing checklist per department, and position suggestions. It leaves renamed and edited content alone.",
  "Веса опыта задаются только на чистой установке: там, где руководитель уже настроил их сам, пакет молчит, чтобы не пересчитать чужой опыт.": "Experience weights are set only on a clean install: where a manager already tuned them, the pack stays silent so it does not rewrite someone else's experience.",
  "Применить пакет": "Apply the pack",
  "Вернуть слова пакета": "Restore the pack words",
  "Вернуть слова пакета поверх ваших правок? Переименованные подписи будут заменены.": "Restore the pack words over your edits? Renamed labels will be replaced.",
  "Применять пакет может руководитель или администратор организации.": "Only a manager or the organization admin can apply the pack.",
  "Готово: слова — {terms}, чек-листы — {templates}.": "Done: {terms} words, {templates} checklists.",
  "Веса опыта обновлены.": "Experience weights updated.",
  "Демо-данные": "Demo data",
  "Покажем на ваших данных за 5 минут": "Show it on your data in five minutes",
  "Демо «{industry}»": "“{industry}” demo",
  "Тестовые сотрудники помечены «{prefix}» и не смешиваются с боевыми данными: их следы можно спрятать из ленты одним тумблером, а людей — удалить целиком.": "Test employees carry the “{prefix}” marker and never mix with real data: one switch hides their traces from the feed, and the people can be deleted entirely.",
  "Людей: {n}": "People: {n}",
  "Чек-листов: {n}": "Checklists: {n}",
  "Поручений: {n}": "Tasks: {n}",
  "Ответов: {n}": "Answers: {n}",
  "Что покажем": "What you will see",
  "Ленту смены, закрытые чек-листы, поручения в работе, опоздания и обратную связь. Состав — из отраслевого пакета, поэтому салон видит мастеров, а не поваров.": "A shift feed, closed checklists, tasks in progress, late arrivals and feedback. The cast comes from the industry pack, so a salon sees stylists rather than cooks.",
  "Обновить состав демо": "Refresh the demo team",
  "Завести демо-сотрудников": "Create demo employees",
  "Сгенерировать активность за 3 смены": "Generate three shifts of activity",
  "Видимость и уборка": "Visibility and cleanup",
  "Спрятать следы демо из ленты": "Hide demo traces from the feed",
  "Показать следы демо в ленте": "Show demo traces in the feed",
  "Удалить следы демо: чек-листы, поручения, фото и обратную связь? Сами тестовые люди останутся.": "Delete demo traces — checklists, tasks, photos and feedback? The test people themselves stay.",
  "Удалить только следы": "Delete traces only",
  "Удалить демо-сотрудников вместе с их активностью? Боевые данные не тронутся.": "Delete the demo employees together with their activity? Real data is not touched.",
  "Удалить демо-сотрудников": "Delete demo employees",
  "Демо готово: {n} тестовых сотрудников из пакета «{industry}».": "Demo ready: {n} test employees from the “{industry}” pack.",
  "Смены закрыты: чек-листов — {runs}, поручений — {tasks}.": "Shifts generated: {runs} checklists, {tasks} tasks.",
  "Следы демо удалены.": "Demo traces deleted.",
  "Показать приложение на тестовых людях за пять минут": "Show the app on test people in five minutes",
  "Кого позвать первым?": "Who should join first?",
  "Сотрудник откроет ссылку и попадёт в вашу организацию. Почту можно не указывать — тогда передайте ссылку лично.": "They open the link and land in your organization. Email is optional — you can hand the link over in person.",
  "Создать приглашение": "Create invite",
  "Ссылка-приглашение": "Invite link",
  "Копировать": "Copy",
  "Приглашений создано: {n}": "Invites created: {n}",
  "Готовые приглашения всегда в разделе «Приглашения».": "Open invites always live in the Invitations section.",
  "Рабочая неделя": "Working week",
  "Пять дней": "Five days",
  "Шесть дней": "Six days",
  "Каждый день": "Every day",
  "Сохранить шаблон недели": "Save the week template",
  "Шаблон сохранён. Смены поставлены: {n}.": "Template saved. Shifts scheduled: {n}.",
  "Шаблон сохранён. Поставим смены, когда появятся люди.": "Template saved. Shifts go in as soon as people join.",
  "Смены и часы можно поправить в табеле в любой момент.": "Shifts and hours can be edited in the timesheet anytime.",
  "Что уже готово": "What is ready",
  "Шаблоны смен": "Shift templates",
  "Стартовые чек-листы": "Starter checklists",
  "Первый чек-лист уже ждёт сотрудников в разделе «Чек-лист».": "The first checklist is waiting for employees in the Checklist section.",
  "Чек-листов пока нет — пакет отрасли заведёт их на последнем шаге.": "No checklists yet — the industry pack creates them on the last step.",
  "Пока пусто — для новой организации это нормально.": "Empty for now — normal for a new organization.",
  "Люди": "People",
  "Должность": "Position",
  "Пока без подразделения": "No department for now",
  "Тариф и лимиты": "Plan and limits",
  "Места, подразделения и срок пробного периода": "Seats, departments and the trial period",
  "Ваш тариф": "Your plan",
  "Пробный": "Trial",
  "Бизнес": "Business",
  "Сеть": "Network",
  "{used} из {limit}": "{used} of {limit}",
  "Осталось {days} дн.": "{days} days left",
  "Пробный период закончился. Люди и данные на месте, но новых сотрудников и подразделений добавить нельзя.": "The trial is over. People and data are safe, but new employees and departments cannot be added.",
  "Когда места кончатся": "When you run out of room",
  "Смена и работа продолжается: закрывается только добавление новых людей и подразделений. Чтобы поднять лимит, напишите нам — переключим тариф вручную.": "Shifts and work continue: only adding new people and departments is closed. To raise the limit, contact us and we will switch the plan manually.",
  "Пригласить человека": "Invite a person",
  "Не удалось узнать тариф — обнови экран.": "Could not load the plan — refresh the screen.",
  "Оплата и счета": "Billing and invoices",
  "Тарифы, счёт на оплату и история платежей": "Plans, invoices and payment history",
  "Оплата не требуется: тариф действует без срока.": "No payment required: the plan has no end date.",
  "Подписка оплачена до {date}": "Subscription paid until {date}",
  "Оплата закончилась. Люди и данные на месте, но новых сотрудников и подразделений добавить нельзя.": "Payment has ended. People and data are safe, but new employees and departments cannot be added.",
  "Тарифы": "Plans",
  "Цена за месяц или за год. Год — десять месяцев.": "Price per month or per year. A year costs ten months.",
  "До {seats} сотрудников, {branches} подразделений": "Up to {seats} employees, {branches} departments",
  "Счёт за месяц": "Invoice for a month",
  "Счёт за год · {amount}": "Invoice for a year · {amount}",
  "Счета": "Invoices",
  "Счетов пока нет — выставьте первый на нужный тариф.": "No invoices yet — create the first one for the plan you need.",
  "Ожидает оплаты": "Awaiting payment",
  "Ждёт подтверждения": "Awaiting confirmation",
  "Оплачен": "Paid",
  "Отменён": "Canceled",
  "Просрочен": "Expired",
  "Оплатить до {date}": "Pay by {date}",
  "Оплачено до {date}": "Paid until {date}",
  "Я оплатил": "I have paid",
  "Отменить": "Cancel",
  "Оплата по счёту: после перевода нажмите «Я оплатил» — мы подтвердим и продлим подписку. Списаний с карты нет.": "Payment by invoice: after the transfer press “I have paid” and we will confirm it and extend the subscription. No card charges.",
  "Не удалось загрузить счета — обнови экран.": "Could not load invoices — refresh the screen.",
  "Платформа": "Platform",
  "Клиенты, тарифы, блокировки и подтверждение оплат": "Clients, plans, blocks and payment confirmations",
  "Клиенты": "Clients",
  "Организаций": "Organizations",
  "На триале": "On trial",
  "Ждут оплаты": "Awaiting payment",
  "К оплате": "Due",
  "Активна": "Active",
  "Места {used} из {limit}": "Seats {used} of {limit}",
  "Срок: {date}": "Until: {date}",
  "Оплата закончилась": "Payment has ended",
  "Заблокировать": "Block",
  "Разблокировать": "Unblock",
  "Заблокирован": "Blocked",
  "Тариф": "Plan",
  "Счета клиентов": "Client invoices",
  "Подтвердить оплату": "Confirm payment",
  "Поиск по названию или коду": "Search by name or code",
  "Ничего не найдено": "Nothing found",
  "Не удалось загрузить платформу — обнови экран.": "Could not load the platform — refresh the screen.",
  "Счетов, ждущих подтверждения, нет.": "No invoices awaiting confirmation.",
  "Бренд": "Brand",
  "Своё имя, цвет и логотип вместо SemiTaskBot": "Your own name, color and logo instead of SemiTaskBot",
  "Сейчас команда видит": "What the team sees now",
  "Бот: @{username}": "Bot: @{username}",
  "Цвет": "Color",
  "Цвет в формате #1c6fb8": "Color in #1c6fb8 format",
  "Логотип (ссылка https)": "Logo (https link)",
  "Сбросить бренд": "Reset brand",
  "Название, цвет и логотип применяются сразу после сохранения — у всей команды, а не только у вас.": "Name, color and logo apply right after saving — for the whole team, not just you.",
  "Проверьте цвет: нужен формат #1c6fb8.": "Check the color: #1c6fb8 format is required.",
  "Логотип должен быть https-ссылкой на картинку.": "The logo must be an https link to an image.",
  "Название не может быть пустым.": "The name cannot be empty.",
  "Не удалось загрузить бренд — обнови экран.": "Could not load the brand — refresh the screen.",
  "Данные и приватность": "Data and privacy",
  "Что хранит сервер, сколько и кто это видел": "What the server keeps, for how long and who accessed it",
  "Сроки хранения": "Retention",
  "Журнал доступа": "Access log",
  "Корзина уволенных": "Fired employees trash",
  "Отсрочка удаления": "Deletion grace period",
  "Копии бэкапов": "Backup copies",
  "{days} дней": "{days} days",
  "Копия данных": "Copy of the data",
  "Структура, люди, доступы и счета — файл JSON. Выгрузка попадает в журнал.": "Structure, people, access and invoices — a JSON file. Every export is logged.",
  "Скачиваем…": "Downloading…",
  "Скачать копию данных": "Download a copy of the data",
  "Данные выгружены — файл скачался.": "Data exported — the file has been downloaded.",
  "Удаление организации": "Delete the organization",
  "Удаление запланировано: данные исчезнут через {days} дн.": "Deletion scheduled: the data will be gone in {days} days.",
  "Пока срок не вышел, доступ у команды закрыт, а отменить можно здесь.": "Until the deadline the team has no access, and you can cancel it here.",
  "Отменить удаление": "Cancel deletion",
  "Данные удаляются через {days} дн. после запроса — всё это время удаление можно отменить.": "Data is deleted {days} days after the request — you can cancel the deletion any time before that.",
  "Код организации": "Organization code",
  "Введите код {slug}, чтобы подтвердить — это необратимо.": "Type {slug} to confirm — this cannot be undone.",
  "Запросить удаление": "Request deletion",
  "Удаление отменено.": "Deletion canceled.",
  "Последние события доступа": "Recent access events",
  "Пока никто не выгружал данные и не менял доступы.": "No one has exported data or changed access yet.",
  "Не удалось загрузить состояние — обнови экран.": "Could not load the state — refresh the screen.",
  "Выгрузка данных": "Data exported",
  "Запрошено удаление": "Deletion requested",
  "Удаление отменено": "Deletion canceled",
  "Смена роли": "Role changed",
  "Создано приглашение": "Invitation created",
  "Отозвано приглашение": "Invitation revoked",
};
